/* eslint-disable react/prop-types */
import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Download, AlertTriangle, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { _sanitizeSvg, VERDICT_COLORS, VerdictPill, Tag, SectionTitle, Caption } from './SharedDisplay.jsx';
// Note: _sanitizeSvg / VERDICT_COLORS / VerdictPill / Tag / SectionTitle /
// Caption were inline at lines 15-82 prior to Sub-Batch 3M (Feb 26 2026).
// Extracted to ./SharedDisplay.jsx as a pure visual extraction; zero
// behavioural change.  Kept the DOMPurify import here too since some
// downstream inline svg-rendering code uses it directly.

// Sub-Batch 3M: shared visual helpers moved to ./SharedDisplay.jsx
// (_sanitizeSvg, VERDICT_COLORS, VerdictPill, Tag, SectionTitle, Caption).
// Kept the comment so future agents know where to find them.

// ──────────────────────────────────────────────────────────────────
// Sub-Batch 3V (Jun 7 2026) — Robust figure download handler.
//
// Replaces bare `<a href target="_blank">` for SVG/PNG downloads.
// Using `<a>` with `target="_blank"` had three failure modes the
// user hit:
//   (a) browsers ignored the response and opened the SVG inline in
//       a new tab instead of saving to disk;
//   (b) a 4xx/5xx response from the API rendered as a blank or JSON
//       page in the new tab, surfacing as "404 page" to the user
//       even when the real cause was an expired session or a
//       mismatched analysis_id;
//   (c) the user lost the context of which figure failed.
//
// The new helper:
//   • fetches via axios (so cookie-based auth flows correctly);
//   • returns a real Blob and saves with a sensible filename;
//   • surfaces structured errors inline as a toast-style message
//     instead of letting the failure escape to a blank tab.
// ──────────────────────────────────────────────────────────────────
async function downloadFigure(url, filename, onError) {
  try {
    const resp = await axios.get(url, {
      responseType: 'blob',
      withCredentials: true,
      headers: { Accept: 'image/svg+xml, image/png, */*' },
    });
    const blob = new Blob([resp.data], {
      type: resp.headers['content-type'] || 'application/octet-stream',
    });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    return true;
  } catch (e) {
    const status = e?.response?.status;
    let msg;
    if (status === 401) {
      msg = 'Session expired — please log in again and retry the download.';
    } else if (status === 404) {
      msg = 'Figure not found. The analysis result may have been removed, or the General-Analysis section was not generated for this upload. Try re-running the analysis on the original CSV.';
    } else if (status === 413) {
      msg = 'The requested figure is too large to render server-side. Re-run with fewer traits, then retry.';
    } else {
      msg = e?.response?.data?.detail || e?.message || 'Download failed';
    }
    if (onError) onError(msg);
    return false;
  }
}

function DownloadButton({ url, filename, label, color = '#4A7C59', testId }) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
      <button
        data-testid={testId}
        disabled={busy}
        onClick={async () => {
          setErr(null);
          setBusy(true);
          await downloadFigure(url, filename, setErr);
          setBusy(false);
        }}
        style={{
          fontSize: 10,
          padding: '4px 8px',
          border: `1px solid ${color}`,
          color: '#1F3A2E',
          background: busy ? '#EFEFEF' : 'white',
          borderRadius: 4,
          cursor: busy ? 'wait' : 'pointer',
        }}
      >
        {busy ? '…' : label}
      </button>
      {err && (
        <span
          data-testid={testId ? `${testId}-error` : undefined}
          style={{ fontSize: 9, color: '#B0413E', marginTop: 2, maxWidth: 220 }}
        >
          {err}
        </span>
      )}
    </span>
  );
}

// ── Sub-components ───────────────────────────────────────────────
const QADashboard = ({ qa }) => {
  if (!qa) return null;
  return (
    <Card style={{ padding: 20, background: '#FAF8F2', border: '1px solid #D6DDD8' }}
          data-testid="gae-qa-dashboard">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: '#6B7B72' }}>
            Publication readiness
          </div>
          <div style={{ fontSize: 36, fontWeight: 700, color: '#1F3A2E', fontFamily: '"Cormorant Garamond", serif' }}
               data-testid="gae-qa-score">
            {qa.total_score} <span style={{ color: '#6B7B72', fontSize: 22 }}>/ {qa.total_max}</span>
            <span style={{ marginLeft: 14, fontSize: 14, color: '#3F4D44' }}>({qa.score_100}/100)</span>
          </div>
        </div>
        <VerdictPill color={qa.verdict_color} />
      </div>
      <p style={{ fontStyle: 'italic', color: '#3F4D44', margin: '8px 0 16px', fontSize: 13 }}>
        {qa.verdict}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table data-testid="gae-qa-breakdown" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#EFEAE0', textAlign: 'left' }}>
              <th style={{ padding: '6px 8px' }}>Category</th>
              <th style={{ padding: '6px 8px', width: 100 }}>Score</th>
              <th style={{ padding: '6px 8px' }}>Detail</th>
              <th style={{ padding: '6px 8px' }}>Reference</th>
            </tr>
          </thead>
          <tbody>
            {(qa.breakdown || []).map((b, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #E4E1D6' }}>
                <td style={{ padding: '6px 8px', fontWeight: 600 }}>{b.category}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>
                  {b.score === null ? <span style={{ color: '#888' }}>—</span> : `${b.score} / ${b.max}`}
                </td>
                <td style={{ padding: '6px 8px', color: '#3F4D44' }}>{b.detail}</td>
                <td style={{ padding: '6px 8px', color: '#6B7B72', fontStyle: 'italic' }}>{b.reference}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {qa.top_action_items && qa.top_action_items.length > 0 && (
        <div style={{ marginTop: 18 }} data-testid="gae-qa-action-items">
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: '#6B7B72', marginBottom: 8 }}>
            Top action items
          </div>
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            {qa.top_action_items.map((a, i) => (
              <li key={i} style={{ marginBottom: 8, fontSize: 13, color: '#3F4D44' }}
                  data-testid={`gae-qa-action-${i + 1}`}>
                <strong style={{ color: '#1F3A2E' }}>P{a.priority} · {a.category}: </strong>
                {a.message}
                {a.reference && (
                  <em style={{ color: '#6B7B72' }}> [{a.reference}]</em>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
      <Caption>{qa.caption}</Caption>
    </Card>
  );
};

const VarianceComponentsTable = ({ vc }) => {
  if (!vc?.rows) return null;
  return (
    <div data-testid="gae-variance-components">
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#EFEAE0' }}>
              {['Trait','Mean','MSg','MSe','σ²g','σ²e','σ²p','H² (broad)','F','p','Sig'].map((h) => (
                <th key={h} style={{ padding: '6px 8px', textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vc.rows.map((r) => (
              <tr key={r.trait} style={{ borderBottom: '1px solid #E4E1D6' }}>
                <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.trait}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.mean}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.ms_genotype}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.ms_error}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.sigma2_g}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.sigma2_e}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.sigma2_p}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.h2_broad}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.f_value}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.p_value}</td>
                <td style={{ padding: '6px 8px' }}>
                  <Tag kind={r.significance === 'ns' ? 'neutral' : 'ok'}>{r.significance}</Tag>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Caption>{vc.caption}</Caption>
    </div>
  );
};

const CorrelationMatrixTable = ({ matrix, title }) => {
  if (!matrix) return null;
  const traits = Object.keys(matrix);
  return (
    <div style={{ marginBottom: 18 }} data-testid={`gae-corr-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#1F3A2E', marginBottom: 6 }}>{title}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#EFEAE0' }}>
              <th style={{ padding: '5px 8px', textAlign: 'left' }}>Trait</th>
              {traits.map((c) => (<th key={c} style={{ padding: '5px 8px' }}>{c}</th>))}
            </tr>
          </thead>
          <tbody>
            {traits.map((rname) => (
              <tr key={rname} style={{ borderBottom: '1px solid #E4E1D6' }}>
                <td style={{ padding: '5px 8px', fontWeight: 600 }}>{rname}</td>
                {traits.map((cname) => {
                  const v = matrix[rname]?.[cname];
                  const isOOB = v != null && (v > 1 || v < -1);
                  return (
                    <td key={cname} style={{
                      padding: '5px 8px', fontFamily: 'monospace',
                      color: isOOB ? '#B11F1F' : '#3F4D44',
                      fontWeight: isOOB ? 700 : 400,
                    }}>{v == null ? '—' : v.toFixed(4)}</td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const MatrixDiagnosticsTable = ({ md }) => {
  if (!md) return null;
  const rows = [
    ['Pearson (A_pearson)', md.pearson],
    ['Phenotypic (A_p)', md.phenotypic],
    ['Genotypic (A_g, bounded — primary)', md.genotypic],
  ];
  const raw = md.genotypic?.raw_unbounded_diagnostics;
  return (
    <div data-testid="gae-matrix-diagnostics">
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#EFEAE0' }}>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Predictor matrix</th>
              <th style={{ padding: '6px 8px' }}>Status</th>
              <th style={{ padding: '6px 8px' }}>det</th>
              <th style={{ padding: '6px 8px' }}>λ min</th>
              <th style={{ padding: '6px 8px' }}>λ max</th>
              <th style={{ padding: '6px 8px' }}>cond #</th>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Collinearity</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, d]) => d ? (
              <tr key={label} style={{ borderBottom: '1px solid #E4E1D6' }}>
                <td style={{ padding: '6px 8px', fontWeight: 600 }}>{label}</td>
                <td style={{ padding: '6px 8px' }}>
                  <Tag kind={d.status === 'INVALID' ? 'invalid' : d.status === 'MARGINAL' ? 'marginal' : 'valid'}>
                    {d.status}
                  </Tag>
                </td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{d.determinant}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: d.smallest_eigenvalue < 0 ? '#B11F1F' : '#3F4D44' }}>
                  {d.smallest_eigenvalue}
                </td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{d.largest_eigenvalue}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{d.condition_number_display}</td>
                <td style={{ padding: '6px 8px' }}>{d.collinearity_band}</td>
              </tr>
            ) : null)}
            {raw && (
              <tr style={{ borderBottom: '1px solid #E4E1D6', background: '#FAF6EE' }}
                  data-testid="gae-matrix-diag-raw">
                <td style={{ padding: '6px 8px', fontWeight: 600, fontStyle: 'italic' }}>Genotypic (A_g, raw — supplementary)</td>
                <td style={{ padding: '6px 8px' }}>
                  <Tag kind={raw.is_positive_definite ? 'valid' : 'invalid'}>
                    {raw.is_positive_definite ? 'VALID' : 'INVALID'}
                  </Tag>
                </td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{raw.determinant}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: raw.smallest_eigenvalue < 0 ? '#B11F1F' : '#3F4D44' }}>
                  {raw.smallest_eigenvalue}
                </td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{raw.largest_eigenvalue}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{raw.is_positive_definite ? raw.condition_number : '∞'}</td>
                <td style={{ padding: '6px 8px', color: '#6B7B72', fontStyle: 'italic' }}>raw / unbounded</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Caption>{md.caption}</Caption>
    </div>
  );
};

const PathTable = ({ path, label, analysisId, pathKey }) => {
  const [svgMarkup, setSvgMarkup] = React.useState(null);
  const [svgError, setSvgError] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!analysisId || !pathKey || !path?.rows) return undefined;
    axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/path-diagram/${analysisId}/${pathKey}.svg`,
      { responseType: 'text', withCredentials: true },
    ).then((r) => {
      if (!cancelled) setSvgMarkup(r.data);
    }).catch(() => {
      if (!cancelled) setSvgError(true);
    });
    return () => { cancelled = true; };
  }, [analysisId, pathKey, path]);

  if (!path?.rows) return null;
  const predictors = path.predictors || [];

  return (
    <div style={{ marginBottom: 16 }} data-testid={`gae-path-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1F3A2E', margin: 0 }}>{label}</h4>
        <div style={{ display: 'flex', gap: 6 }}>
          <Tag kind="neutral">R² = {path.r_squared}</Tag>
          <Tag kind="neutral">Residual = {path.residual_effect_display ? 'Undefined' : path.residual_effect}</Tag>
        </div>
      </div>
      {path.matrix_invalid && path.invalid_warning && (
        <div data-testid={`gae-path-invalid-${label.toLowerCase().replace(/\s/g, '-')}`}
             style={{ background: '#FBEAEA', border: '1px solid #E04848', padding: 10, borderRadius: 6, margin: '8px 0', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <AlertCircle size={18} style={{ color: '#B11F1F', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12, color: '#B11F1F' }}>
            <strong>INVALID — </strong>{path.invalid_warning}
          </div>
        </div>
      )}
      {path.multicollinearity_warning && path.multicollinearity_note && (
        <div style={{ background: '#FFF4E0', border: '1px solid #F4B21A', padding: 10, borderRadius: 6, margin: '8px 0', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <AlertTriangle size={18} style={{ color: '#B45309', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12, color: '#B45309' }}>
            <strong>Multicollinearity — </strong>{path.multicollinearity_note}
          </div>
        </div>
      )}

      {/* Path diagram — inline SVG (vector) */}
      {svgMarkup && !svgError && (
        <div
          data-testid={`gae-path-diagram-${pathKey}`}
          style={{ background: '#FFFFFF', borderRadius: 8, padding: 8, border: '1px solid #E4E1D6', marginBottom: 12, overflow: 'auto' }}
          dangerouslySetInnerHTML={{ __html: _sanitizeSvg(svgMarkup) }}
        />
      )}
      {svgMarkup && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <DownloadButton
            testId={`gae-path-diagram-${pathKey}-svg-download`}
            url={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/path-diagram/${analysisId}/${pathKey}.svg`}
            filename={`path_diagram_${pathKey}.svg`}
            label="Download SVG (vector)"
            color="#4A7C59"
          />
          <DownloadButton
            testId={`gae-path-diagram-${pathKey}-png-download`}
            url={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/path-diagram/${analysisId}/${pathKey}.png`}
            filename={`path_diagram_${pathKey}.png`}
            label="Download PNG (300 DPI)"
            color="#4A7C59"
          />
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#EFEAE0' }}>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Predictor</th>
              <th style={{ padding: '6px 8px' }}>Direct effect</th>
              {predictors.map((p) => (
                <th key={p} style={{ padding: '6px 8px' }}>via {p}</th>
              ))}
              <th style={{ padding: '6px 8px' }}>Total indirect</th>
              <th style={{ padding: '6px 8px' }}>Total corr w/ dep</th>
            </tr>
          </thead>
          <tbody>
            {path.rows.map((r) => (
              <tr key={r.predictor} style={{ borderBottom: '1px solid #E4E1D6' }}>
                <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.predictor}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace',
                             color: Math.abs(r.direct_effect) > 1 ? '#B11F1F' : '#3F4D44',
                             fontWeight: Math.abs(r.direct_effect) > 1 ? 700 : 400 }}>
                  {r.direct_effect}
                </td>
                {predictors.map((p) => (
                  <td key={p} style={{ padding: '6px 8px', fontFamily: 'monospace' }}>
                    {p === r.predictor ? <span style={{ color: '#888' }}>—</span> : r.indirect_via?.[p]}
                  </td>
                ))}
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.total_indirect_effect}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.total_correlation_with_dep}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: '#6B7B72', fontStyle: 'italic', marginTop: 4 }}>
        Source matrix: {path.source_matrix_label} · Dep: {path.dependent_variable}
      </div>
    </div>
  );
};

const VIFTable = ({ vif, label }) => {
  if (!vif?.rows) return null;
  return (
    <div style={{ marginBottom: 12 }} data-testid={`gae-vif-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#1F3A2E', marginBottom: 6 }}>{label}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#EFEAE0' }}>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Predictor</th>
              <th style={{ padding: '6px 8px' }}>VIF</th>
              <th style={{ padding: '6px 8px' }}>Tolerance</th>
              <th style={{ padding: '6px 8px' }}>Band</th>
            </tr>
          </thead>
          <tbody>
            {vif.rows.map((r) => (
              <tr key={r.predictor} style={{ borderBottom: '1px solid #E4E1D6' }}>
                <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.predictor}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.vif}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.tolerance}</td>
                <td style={{ padding: '6px 8px' }}>
                  <Tag kind={r.band?.toLowerCase()}>{r.band}</Tag>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RgSignificanceTable = ({ rg }) => {
  if (!rg?.rows) return null;
  return (
    <div data-testid="gae-rg-significance">
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#EFEAE0' }}>
              {['Trait pair','rg','SE','t','p','95% CI','Sig'].map((h) => (
                <th key={h} style={{ padding: '6px 8px', textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rg.rows.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #E4E1D6' }}>
                <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.trait_1} × {r.trait_2}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.rg}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.se}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.t}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.p}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>
                  [{r.ci_low}, {r.ci_high}]
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <Tag kind={r.significance === 'ns' || r.significance === 'NA' ? 'neutral' : 'ok'}>{r.significance}</Tag>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Caption>df = {rg.df} · {rg.method} · {rg.caption}</Caption>
    </div>
  );
};

// ── Main view ──────────────────────────────────────────────────────
// ── Heatmap panel + Mantel table ──────────────────────────────────
const HeatmapPanel = ({ analysisId, mantelRows, genoInvalid }) => {
  const [svg, setSvg] = React.useState(null);
  const [err, setErr] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    if (!analysisId) return undefined;
    axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/heatmap/${analysisId}/combined.svg`,
      { responseType: 'text', withCredentials: true },
    ).then((r) => { if (!cancelled) setSvg(r.data); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [analysisId]);

  return (
    <div data-testid="gae-heatmap-panel">
      {svg && !err && (
        <div
          data-testid="gae-heatmap-combined-svg"
          style={{ background: '#FFFFFF', borderRadius: 8, padding: 8, border: '1px solid #E4E1D6', marginBottom: 8, overflow: 'auto' }}
          dangerouslySetInnerHTML={{ __html: _sanitizeSvg(svg) }}
        />
      )}
      {svg && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {['combined','pearson','phenotypic','genotypic'].map((k) => (
            <DownloadButton
              key={k}
              testId={`gae-heatmap-${k}-svg-download`}
              url={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/heatmap/${analysisId}/${k}.svg`}
              filename={`heatmap_${k}.svg`}
              label={`Download ${k} SVG`}
              color="#4A7C59"
            />
          ))}
        </div>
      )}
      <Caption>
        Diverging blue → white → red color scheme. Numerical correlation values in each cell; significance asterisks shown as superscripts (*** p&lt;0.001, ** p&lt;0.01, * p&lt;0.05).
        {genoInvalid && ' Genotypic panel carries the MATRIX NOT POSITIVE DEFINITE overlay — asterisks omitted as significance cannot be reliably computed on a non-PD matrix.'}
      </Caption>

      {mantelRows && mantelRows.length > 0 && (
        <>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1F3A2E', margin: '14px 0 4px' }}>
            C2. Mantel Matrix-Structure Comparison
          </h4>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="gae-mantel-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#EFEAE0' }}>
                  {['Comparison','Mantel r','p (perm)','n perm','Interpretation'].map((h) => (
                    <th key={h} style={{ padding: '6px 8px', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mantelRows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #E4E1D6' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.comparison}</td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.mantel_r}</td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.p_value_perm}</td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.n_perm}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <Tag kind={r.color === 'green' ? 'ok' : r.color === 'yellow' ? 'moderate' : 'invalid'}>
                        {r.interpretation}
                      </Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Caption>
            Mantel (1967) test on flattened upper-triangle off-diagonal cells; 999 row/col permutations (Smouse, Long & Sokal 1986). High Mantel r indicates the two correlation matrices share the same pattern of trait relationships.
          </Caption>
        </>
      )}
    </div>
  );
};

// ── PCA Biplot panel (Sub-Batch 2C) ────────────────────────────
const BiplotPanel = ({ analysisId }) => {
  const [alpha, setAlpha] = React.useState(0.0);
  const [pcPair, setPcPair] = React.useState('1-2');
  const [clusterMethod, setClusterMethod] = React.useState('tocher');
  const [svg, setSvg] = React.useState(null);
  const [variance, setVariance] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!analysisId) return undefined;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/biplot/${analysisId}.svg?alpha=${alpha}&pc_pair=${pcPair}&cluster_method=${clusterMethod}`;
    axios.get(url, { responseType: 'text', withCredentials: true })
      .then((r) => { if (!cancelled) setSvg(r.data); })
      .catch(() => { if (!cancelled) setSvg(null); });
    return () => { cancelled = true; };
  }, [analysisId, alpha, pcPair, clusterMethod]);

  React.useEffect(() => {
    let cancelled = false;
    if (!analysisId) return undefined;
    axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/biplot-variance/${analysisId}?cluster_method=${clusterMethod}`,
      { withCredentials: true },
    ).then((r) => { if (!cancelled) setVariance(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [analysisId, clusterMethod]);

  const captionAlpha0 = (
    'Correlation biplot (\u03B1=0): trait vector angles approximate inter-trait correlations: cos \u03B8 \u2248 r(trait_i, trait_j). '
    + 'Vectors pointing same direction = positive correlation; perpendicular = independent; opposite = negative correlation. '
    + 'A trait vector tip lying exactly on the unit circle indicates 100% of that trait\u2019s variance is captured by the displayed PCs. '
    + 'Vectors well inside the circle have substantial variance on PCs not shown \u2014 interpret with caution. '
    + 'Genotype positions reflect normalized scores; close genotypes have similar trait profiles. Reference: Gabriel (1971), Greenacre (2010).'
  );
  const captionAlpha1 = (
    'Covariance biplot (\u03B1=1): trait vector lengths scale with each trait\u2019s standard deviation. '
    + 'Longer vectors indicate traits with more variation across the genotype panel. '
    + 'Genotype-to-genotype Euclidean distance on the biplot approximates true multivariate distance (analogous to Mahalanobis D\u00B2). '
    + 'Two genotypes plotted close together have similar overall multivariate phenotype. '
    + 'No unit reference circle is shown because variance scaling is already embedded in vector lengths. Reference: Gabriel (1971); Greenacre (2010).'
  );
  const captionTocher = (
    ' Genotype dots colored by Tocher\u2019s optimization clustering (Rao 1952; Singh & Chaudhary 1985 \u00A713) on Mahalanobis-style '
    + 'D\u00B2 distances of standardized entry means \u2014 same algorithm used in the D\u00B2 Genetic Divergence section. '
    + 'Tocher is the Indian breeding convention for divergence clustering.'
  );
  const captionWard = (
    ' Genotype dots colored by Ward linkage at k=4 on standardized entry means (Husson, L\u00EA & Pag\u00E8s 2017) '
    + '\u2014 not Tocher. Chosen for visually balanced cluster sizes when Tocher produces a mega-cluster.'
  );

  const tocherWarn = (clusterMethod === 'tocher' && variance?.tocher_imbalanced) ? (
    <div
      data-testid="gae-biplot-tocher-imbalance-note"
      style={{
        background: '#FFF6E0', border: '1px solid #E5C46B',
        borderRadius: 6, padding: '6px 10px', marginBottom: 8,
        fontSize: 11, color: '#8B5A0A', fontStyle: 'italic',
      }}
    >
      {'Tocher produced '}
      <strong>{variance?.tocher_largest_cluster_size}</strong>
      {' of '}{variance?.n_genotypes}{' genotypes ('}
      <strong>{variance?.tocher_largest_cluster_pct}%</strong>
      {') in the largest cluster \u2014 consider switching to Ward linkage for more balanced visual coloring.'}
    </div>
  ) : null;

  return (
    <div data-testid="gae-biplot-panel">
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <div data-testid="gae-biplot-mode-toggle" style={{ display: 'flex', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#1F3A2E' }}>Mode:</label>
          {[
            { v: 0.0, label: 'Correlation (\u03B1=0)' },
            { v: 1.0, label: 'Covariance (\u03B1=1)' },
          ].map((opt) => (
            <label key={opt.v} style={{ fontSize: 12, color: '#3F4D44', cursor: 'pointer' }}>
              <input
                type="radio"
                checked={Math.abs(alpha - opt.v) < 1e-6}
                onChange={() => setAlpha(opt.v)}
                style={{ marginRight: 4 }}
                data-testid={`gae-biplot-alpha-${opt.v === 0 ? '0' : '1'}`}
              />
              {opt.label}
            </label>
          ))}
        </div>
        <div data-testid="gae-biplot-pc-pair" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#1F3A2E' }}>PCs:</label>
          <select
            value={pcPair}
            onChange={(e) => setPcPair(e.target.value)}
            style={{ fontSize: 12, padding: '2px 6px', border: '1px solid #CCCCCC', borderRadius: 4 }}
            data-testid="gae-biplot-pc-pair-select"
          >
            <option value="1-2">PC1 vs PC2</option>
            <option value="1-3">PC1 vs PC3</option>
            <option value="2-3">PC2 vs PC3</option>
          </select>
        </div>
        <div data-testid="gae-biplot-cluster-toggle" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#1F3A2E' }}>Cluster colors:</label>
          {[
            { v: 'tocher', label: 'Tocher (default, Rao 1952)' },
            { v: 'ward', label: 'Ward linkage k=4' },
          ].map((opt) => (
            <label key={opt.v} style={{ fontSize: 12, color: '#3F4D44', cursor: 'pointer' }}>
              <input
                type="radio"
                checked={clusterMethod === opt.v}
                onChange={() => setClusterMethod(opt.v)}
                style={{ marginRight: 4 }}
                data-testid={`gae-biplot-cluster-${opt.v}`}
              />
              {opt.label}
            </label>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { mode: 0, pair: '1-2', label: '\u03B1=0, PC1\u00D7PC2' },
            { mode: 1, pair: '1-2', label: '\u03B1=1, PC1\u00D7PC2' },
            { mode: 0, pair: '1-3', label: '\u03B1=0, PC1\u00D7PC3' },
            { mode: 1, pair: '2-3', label: '\u03B1=1, PC2\u00D7PC3' },
          ].map((opt) => (
            <DownloadButton
              key={`${opt.mode}-${opt.pair}`}
              testId={`gae-biplot-download-${opt.mode}-${opt.pair}`}
              url={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/biplot/${analysisId}.svg?alpha=${opt.mode}.0&pc_pair=${opt.pair}&cluster_method=${clusterMethod}`}
              filename={`biplot_alpha${opt.mode}_pc${opt.pair.replace('-', '_')}.svg`}
              label={`SVG ${opt.label}`}
              color="#4A7C59"
            />
          ))}
        </div>
      </div>
      {tocherWarn}
      {svg && (
        <div
          data-testid="gae-biplot-svg"
          style={{ background: '#FFFFFF', borderRadius: 8, padding: 8, border: '1px solid #E4E1D6', marginBottom: 8, overflow: 'auto' }}
          dangerouslySetInnerHTML={{ __html: _sanitizeSvg(svg) }}
        />
      )}
      <Caption>
        {Math.abs(alpha) < 1e-6 ? captionAlpha0 : captionAlpha1}
        {clusterMethod === 'tocher' ? captionTocher : captionWard}
      </Caption>

      {variance?.variance_table && (
        <div style={{ marginTop: 8 }}>
          <h5 style={{ fontSize: 13, fontWeight: 600, color: '#1F3A2E', margin: '8px 0 4px' }}>
            Variance Explained by Principal Components
          </h5>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="gae-biplot-variance-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#EFEAE0' }}>
                  {['PC','Variance %','Cumulative %','Eigenvalue','Displayed'].map((h) => (
                    <th key={h} style={{ padding: '5px 8px', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {variance.variance_table.slice(0, 6).map((r) => (
                  <tr key={r.PC} style={{ borderBottom: '1px solid #E4E1D6', background: r.displayed ? '#FAF6EE' : 'transparent' }}>
                    <td style={{ padding: '5px 8px', fontWeight: 600 }}>{r.PC}</td>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{r.variance_pct}</td>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{r.cumulative_pct}</td>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{r.eigenvalue}</td>
                    <td style={{ padding: '5px 8px', fontSize: 11, color: '#4A7C59' }}>{r.displayed ? '\u2605 Currently displayed' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Caption>
            Cluster coloring on the biplot uses
            {' '}
            {variance?.clustering_method ?? (clusterMethod === 'tocher' ? 'Tocher\u2019s optimization (Rao 1952)' : 'Ward (k=4) on standardized entry means')}.
            {' '}
            Genotype labels can be force-positioned to avoid overlap.
            {' '}
            <strong>Exports (PDF/DOCX) default to Tocher</strong> to match Indian breeding convention; switch to Ward in this panel for visualisation purposes.
          </Caption>
        </div>
      )}
    </div>
  );
};

const DendrogramPanel = ({ analysisId }) => {
  const [summary, setSummary] = React.useState(null);
  const [svg, setSvg] = React.useState(null);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!analysisId) return undefined;
    axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/dendrogram-summary/${analysisId}`,
      { withCredentials: true },
    ).then((r) => { if (!cancelled) setSummary(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || 'Failed to load dendrogram summary'); });
    return () => { cancelled = true; };
  }, [analysisId]);

  React.useEffect(() => {
    let cancelled = false;
    if (!analysisId) return undefined;
    axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/dendrogram/${analysisId}/combined.svg`,
      { responseType: 'text', withCredentials: true },
    ).then((r) => { if (!cancelled) setSvg(r.data); })
      .catch(() => { if (!cancelled) setSvg(null); });
    return () => { cancelled = true; };
  }, [analysisId]);

  if (err) {
    return (
      <div data-testid="gae-dendrogram-error" style={{ padding: 10, background: '#FBEAEA', borderRadius: 6, color: '#B11F1F', fontSize: 12 }}>
        {err}
      </div>
    );
  }

  const renderCop = (panel) => {
    if (!panel) return null;
    return (
      <tr style={{ borderBottom: '1px solid #E4E1D6' }}>
        <td style={{ padding: '5px 8px' }}>{panel.method}</td>
        <td style={{ padding: '5px 8px', fontFamily: 'monospace', color: panel.cophenetic_band_color, fontWeight: 600 }}>
          {panel.cophenetic_rho}
        </td>
        <td style={{ padding: '5px 8px', fontSize: 11, color: panel.cophenetic_band_color }}>{panel.cophenetic_band}</td>
        <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{panel.cut_threshold}</td>
        <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{panel.n_clusters_at_cut}</td>
      </tr>
    );
  };

  return (
    <div data-testid="gae-dendrogram-panel">
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {[
          { kind: 'combined', label: 'Combined (UPGMA + Ward)' },
          { kind: 'upgma', label: 'UPGMA only' },
          { kind: 'ward', label: 'Ward only' },
        ].map((opt) => (
          <DownloadButton
            key={opt.kind}
            testId={`gae-dendrogram-download-${opt.kind}`}
            url={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/dendrogram/${analysisId}/${opt.kind}.svg`}
            filename={`dendrogram_${opt.kind}.svg`}
            label={`SVG: ${opt.label}`}
            color="#4A7C59"
          />
        ))}
      </div>
      {summary?.cophenetic_alignment_note && (
        <div
          data-testid="gae-dendrogram-alignment-note"
          style={{
            background: '#E8F4ED', border: '1px solid #B7DEC6',
            borderRadius: 6, padding: '8px 12px', marginBottom: 10,
            fontSize: 12, color: '#166737', fontStyle: 'italic',
          }}
        >
          {summary.cophenetic_alignment_note}
        </div>
      )}
      {svg && (
        <div
          data-testid="gae-dendrogram-svg"
          style={{ background: '#FFFFFF', borderRadius: 8, padding: 8, border: '1px solid #E4E1D6', marginBottom: 8, overflow: 'auto' }}
          dangerouslySetInnerHTML={{ __html: _sanitizeSvg(svg) }}
        />
      )}
      <Caption>{summary?.caption}</Caption>

      {summary && (
        <>
          <h5 style={{ fontSize: 13, fontWeight: 600, color: '#1F3A2E', margin: '12px 0 4px' }}>
            Cophenetic Correlation (Sokal-Rohlf 1962)
          </h5>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="gae-dendrogram-cophenetic-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#EFEAE0' }}>
                  {['Method','Cophenetic \u03C1','Fit band','Cut threshold','Clusters @ cut'].map((h) => (
                    <th key={h} style={{ padding: '5px 8px', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {renderCop(summary.upgma)}
                {renderCop(summary.ward)}
              </tbody>
            </table>
          </div>
          <Caption>
            {'\u03C1 > 0.80 indicates the dendrogram faithfully represents pairwise distances. UPGMA and Ward should be reported side-by-side; agreement between methods strengthens biological inference.'}
          </Caption>

          {[
            { title: 'UPGMA \u2014 Mahalanobis D\u00B2', rows: summary.composition_table_upgma },
            { title: 'Ward \u2014 standardized Euclidean', rows: summary.composition_table_ward },
          ].map((tbl) => (
            <div key={tbl.title}>
              <h5 style={{ fontSize: 13, fontWeight: 600, color: '#1F3A2E', margin: '12px 0 4px' }}>
                {tbl.title} — cluster composition at 25/50/75 percentile thresholds
              </h5>
              <div style={{ overflowX: 'auto' }}>
                <table data-testid={`gae-dendrogram-composition-${tbl.title.includes('UPGMA') ? 'upgma' : 'ward'}`} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#EFEAE0' }}>
                      <th style={{ padding: '5px 8px', textAlign: 'left' }}>Threshold</th>
                      <th style={{ padding: '5px 8px', textAlign: 'left' }}>Clusters</th>
                      <th style={{ padding: '5px 8px', textAlign: 'left' }}>Members</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(tbl.rows || []).map((r, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #E4E1D6' }}>
                        <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{r.threshold}</td>
                        <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{r.n_clusters}</td>
                        <td style={{ padding: '5px 8px', fontSize: 11 }}>
                          {r.members.map((g, i) => (
                            <span key={i} style={{ marginRight: 8, color: '#3F4D44' }}>
                              {'{'}{g.slice(0, 6).join(', ')}{g.length > 6 ? ', ...' : ''}{'}'} <span style={{ color: '#7A8E83' }}>(n={g.length})</span>
                            </span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

const BootstrapPanel = ({ analysisId, onResult }) => {
  // Default B = 200 (~16s on 50-genotype panels) — fits Cloudflare proxy
  // window. User can opt into 500 / 1000 via the input field. The
  // permanent escape from proxy timeouts on B=1000 is Sub-Batch 3F
  // email-bundle delivery (no proxy involvement on the recipient side).
  const [B, setB] = React.useState(200);
  // Phase 4 #1 (Niranjan, Feb 2026) — deterministic seed: same dataset
  // + same seed = bit-for-bit identical BCa CIs for journal
  // reproducibility audits. Default 20260211 (Niranjan canonical date).
  const [seed, setSeed] = React.useState(20260211);
  const [boot, setBoot] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [svg, setSvg] = React.useState(null);

  const run = async () => {
    if (!analysisId) return;
    setBusy(true);
    setErr(null);
    setBoot(null);
    setSvg(null);
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/bootstrap/${analysisId}`,
        { params: { B, seed }, withCredentials: true, timeout: 180000 },
      );
      setBoot(r.data);
      if (onResult) onResult(r.data);
      const s = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/bootstrap-forest/${analysisId}.svg`,
        { params: { B, seed }, responseType: 'text', withCredentials: true, timeout: 180000 },
      );
      setSvg(s.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Bootstrap failed');
    } finally {
      setBusy(false);
    }
  };

  const renderCI = (ci) => {
    if (!ci || ci.lo == null || ci.hi == null) return null;
    const sig = (ci.lo > 0 && ci.hi > 0) || (ci.lo < 0 && ci.hi < 0);
    const sigColor = sig ? '#166737' : '#7A8E83';
    return (
      <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 11, color: sigColor, fontWeight: sig ? 600 : 400 }}>
        {ci.est >= 0 ? '+' : ''}{ci.est.toFixed(3)}
        {' '}
        <span style={{ color: '#7A8E83', fontWeight: 400 }}>
          [{ci.lo >= 0 ? '+' : ''}{ci.lo.toFixed(3)}, {ci.hi >= 0 ? '+' : ''}{ci.hi.toFixed(3)}]
        </span>
        {sig && <span style={{ marginLeft: 4, color: '#166737' }}>*</span>}
      </td>
    );
  };

  const predictors = boot?.meta?.predictors || [];
  // Memoize the path-type filter — eliminates 4 redundant `.filter()`
  // calls per render that the perf-scanner flagged as "expensive
  // computations in render" (cited lines 1091/1102/1111/1119).
  const _PATH_TYPES = React.useMemo(() => ['pearson', 'phenotypic', 'genotypic'], []);
  const pathTypesPresent = React.useMemo(
    () => _PATH_TYPES.filter((p) => boot?.paths?.[p]?.direct),
    [boot, _PATH_TYPES],
  );
  const pathTypesWithRSquared = React.useMemo(
    () => _PATH_TYPES.filter((p) => boot?.paths?.[p]),
    [boot, _PATH_TYPES],
  );

  return (
    <div data-testid="gae-bootstrap-panel">
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#1F3A2E' }}>Replicates B:</label>
          {[200, 500, 1000].map((v) => (
            <label key={v} style={{ fontSize: 12, color: '#3F4D44', cursor: 'pointer' }}>
              <input
                type="radio"
                checked={B === v}
                onChange={() => setB(v)}
                disabled={busy}
                style={{ marginRight: 4 }}
                data-testid={`gae-bootstrap-B-${v}`}
              />
              {v}{v === 500 ? ' (default)' : (v === 1000 ? ' (Efron-Tibshirani)' : ' (quick)')}
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}
          title="Deterministic seed for reproducibility. Same dataset + same seed = bit-for-bit identical BCa CIs (journal audit-grade). Default 20260211.">
          <label style={{ fontSize: 12, fontWeight: 600, color: '#1F3A2E' }} htmlFor="gae-bootstrap-seed">Seed:</label>
          <input
            id="gae-bootstrap-seed"
            type="number"
            value={seed}
            onChange={(e) => setSeed(parseInt(e.target.value, 10) || 0)}
            disabled={busy}
            style={{ width: 110, fontSize: 12, padding: '4px 6px', border: '1px solid #C8D4CD', borderRadius: 4, fontFamily: 'monospace' }}
            data-testid="gae-bootstrap-seed"
          />
          <button
            type="button"
            onClick={() => setSeed(Math.floor(Date.now() / 1000))}
            disabled={busy}
            style={{ fontSize: 10, padding: '3px 6px', border: '1px solid #C8D4CD', borderRadius: 4, background: 'white', color: '#3F4D44', cursor: busy ? 'not-allowed' : 'pointer' }}
            data-testid="gae-bootstrap-seed-now"
            title="Use current unix timestamp as a fresh seed"
          >
            now
          </button>
        </div>
        <Button
          data-testid="gae-bootstrap-run"
          onClick={run}
          disabled={busy || !analysisId}
          style={{ background: '#2C5530', color: 'white' }}
        >
          {busy ? `Running ${B} replicates...` : `Run Bootstrap (B=${B})`}
        </Button>
        {boot && (
          <DownloadButton
            testId="gae-bootstrap-csv-download"
            url={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/bootstrap-csv/${analysisId}?B=${B}&seed=${seed}`}
            filename={`bootstrap_B${B}.csv`}
            label="CSV (long format)"
            color="#4A7C59"
          />
        )}
        {boot && (
          <DownloadButton
            testId="gae-bootstrap-svg-download"
            url={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/bootstrap-forest/${analysisId}.svg?B=${B}&seed=${seed}`}
            filename={`bootstrap_forest_B${B}.svg`}
            label="SVG forest plot"
            color="#4A7C59"
          />
        )}
        {boot && (
          <DownloadButton
            testId="gae-bootstrap-svg-reviewer-download"
            url={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/bootstrap-forest/${analysisId}.svg?B=${B}&seed=${seed}&annotate_reviewer=true`}
            filename={`bootstrap_forest_B${B}_reviewer.svg`}
            label="SVG (reviewer annotated)"
            color="#6366F1"
          />
        )}
      </div>
      {B === 1000 && !boot && (
        <div data-testid="gae-bootstrap-1000-warn" style={{ padding: 8, background: '#FFF6E0', border: '1px solid #E5C46B', borderRadius: 4, color: '#8B5A0A', fontSize: 11, fontStyle: 'italic', marginBottom: 10 }}>
          {'B = 1000 (Efron-Tibshirani 1993 standard) takes \u224885s for typical datasets and may exceed the hosted preview proxy timeout (\u224860s). Use B = 500 for the same statistical rigor with a faster round-trip; B = 1000 is the gold standard for final manuscript submissions on production deployments.'}
        </div>
      )}
      {err && (
        <div data-testid="gae-bootstrap-err" style={{ padding: 8, background: '#FBEAEA', color: '#B11F1F', borderRadius: 4, fontSize: 12, marginBottom: 10 }}>
          {err}
        </div>
      )}
      {busy && (
        <div data-testid="gae-bootstrap-busy" style={{ padding: 8, background: '#E8F4ED', color: '#166737', borderRadius: 4, fontSize: 12 }}>
          {`Running ${B} bootstrap replicates + ${'\u2248'}50 jackknife replicates. Estimated ~${(B * 0.084).toFixed(0)} seconds...`}
        </div>
      )}
      {boot && svg && (
        <div
          data-testid="gae-bootstrap-forest"
          style={{ background: '#FFFFFF', borderRadius: 8, padding: 8, border: '1px solid #E4E1D6', marginTop: 8, marginBottom: 8, overflow: 'auto' }}
          dangerouslySetInnerHTML={{ __html: _sanitizeSvg(svg) }}
        />
      )}
      {boot && (
        <Caption>{boot.caption}</Caption>
      )}
      {boot && (
        <div style={{ marginTop: 10 }}>
          <h5 style={{ fontSize: 13, fontWeight: 600, color: '#1F3A2E', margin: '8px 0 4px' }}>
            H1. Direct Effects with {boot.meta.ci_pct}% BCa CI
          </h5>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="gae-bootstrap-direct-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#EFEAE0' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>Predictor</th>
                  {pathTypesPresent.map((p) => (
                      <th key={p} style={{ padding: '5px 8px', textAlign: 'left', textTransform: 'capitalize' }}>{p}</th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {predictors.map((pp) => (
                  <tr key={pp} style={{ borderBottom: '1px solid #E4E1D6' }}>
                    <td style={{ padding: '5px 8px', fontWeight: 600 }}>{pp}</td>
                    {pathTypesPresent.map((p) => (
                        <React.Fragment key={p}>{renderCI(boot.paths[p].direct[pp])}</React.Fragment>
                      ))}
                  </tr>
                ))}
                <tr style={{ background: '#FAF6EE', borderBottom: '1px solid #C9C2B0' }}>
                  <td style={{ padding: '5px 8px', fontWeight: 600, fontStyle: 'italic' }}>R²</td>
                  {pathTypesWithRSquared.map((p) => (
                      <React.Fragment key={p}>{renderCI(boot.paths[p].r_squared)}</React.Fragment>
                    ))}
                </tr>
                <tr style={{ background: '#FAF6EE' }}>
                  <td style={{ padding: '5px 8px', fontWeight: 600, fontStyle: 'italic' }}>Residual</td>
                  {pathTypesWithRSquared.map((p) => (
                      <React.Fragment key={p}>{renderCI(boot.paths[p].residual_effect)}</React.Fragment>
                    ))}
                </tr>
              </tbody>
            </table>
          </div>
          <Caption>
            {'Green entries: CI excludes zero (statistically supported direct effect). Method per cell: '}
            <strong>{Object.values(boot.paths || {}).flatMap((blob) => Object.values(blob.direct || {}).map((d) => d.method))[0] || 'bca'}</strong>
            {'. Predictors with CI spanning zero should be retained with caution; consider Sequential Path Analysis (Sub-Batch 3C) for selection.'}
          </Caption>

          <h5 style={{ fontSize: 13, fontWeight: 600, color: '#1F3A2E', margin: '12px 0 4px' }}>
            H2. Variance Components with {boot.meta.ci_pct}% BCa CI
          </h5>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="gae-bootstrap-vc-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#EFEAE0' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>Trait</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>σ²g</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>σ²e</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>σ²p</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>h² (broad)</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(boot.variance_components || {}).map(([trait, blob]) => (
                  <tr key={trait} style={{ borderBottom: '1px solid #E4E1D6' }}>
                    <td style={{ padding: '5px 8px', fontWeight: 600 }}>{trait}</td>
                    {renderCI(blob.sigma2_g)}
                    {renderCI(blob.sigma2_e)}
                    {renderCI(blob.sigma2_p)}
                    {renderCI(blob.h2_broad)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Caption>
            {'Heritability CIs clipped to [0, 1] (broad-sense bound). Variance component CIs use cluster-bootstrap on genotypes per Davison & Hinkley (1997) §3.8 — each replicate samples '}
            {boot.meta.n_genotypes}
            {' genotypes with replacement and re-runs the full ANOVA-based VC pipeline. Convergence: '}
            {boot.convergence?.n_successful}
            {' / '}
            {boot.convergence?.n_total}
            {' replicates produced valid VC estimates.'}
          </Caption>
        </div>
      )}

      {/* Phase 4 follow-up — Compare two seeds side-by-side (audit
          gesture). Same dataset + different seeds should produce
          near-identical BCa CIs when B is large enough; if they don't,
          there's a hidden randomness source somewhere in the
          pipeline. Hidden behind a collapsible toggle so it doesn't
          clutter the primary results. */}
      {boot && analysisId && (
        <SeedComparisonPanel analysisId={analysisId} B={B} primarySeed={seed} />
      )}
    </div>
  );
};


/**
 * Compares two BCa bootstrap runs (same dataset, different seeds) so
 * a reviewer can visually confirm that the CIs are sampling-noise
 * stable. Pattern 8 (self-documenting provenance) + Pattern 10
 * (uncertainty-honest decision aids).
 */
const SeedComparisonPanel = ({ analysisId, B, primarySeed }) => {
  const [open, setOpen] = React.useState(false);
  const [seedA, setSeedA] = React.useState(primarySeed);
  const [seedB, setSeedB] = React.useState(primarySeed + 1);
  const [bootA, setBootA] = React.useState(null);
  const [bootB, setBootB] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    setSeedA(primarySeed);
    setSeedB(primarySeed + 1);
  }, [primarySeed]);

  const run = async () => {
    setBusy(true);
    setErr(null);
    setBootA(null);
    setBootB(null);
    try {
      const [rA, rB] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/bootstrap/${analysisId}`,
          { params: { B, seed: seedA }, withCredentials: true, timeout: 180000 },
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/bootstrap/${analysisId}`,
          { params: { B, seed: seedB }, withCredentials: true, timeout: 180000 },
        ),
      ]);
      setBootA(rA.data);
      setBootB(rB.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Seed comparison failed');
    } finally {
      setBusy(false);
    }
  };

  const predictors = bootA?.meta?.predictors || [];
  const ptype = 'phenotypic';
  // Compute |Δ| in the lower CI bound + upper CI bound across the
  // two runs. Treat anything < 5% of |est| as "stable" (sampling
  // noise), 5-15% as "moderate drift", > 15% as "unstable" (suggests
  // B is too small).
  const rows = predictors.map((pp) => {
    const a = bootA?.paths?.[ptype]?.direct?.[pp];
    const b = bootB?.paths?.[ptype]?.direct?.[pp];
    if (!a || !b) return { pp, a, b, verdict: 'n/a', dLo: null, dHi: null };
    const refMag = Math.max(Math.abs(a.est ?? 0), 1e-3);
    const dLo = (b.lo ?? 0) - (a.lo ?? 0);
    const dHi = (b.hi ?? 0) - (a.hi ?? 0);
    const drift = Math.max(Math.abs(dLo), Math.abs(dHi)) / refMag;
    let verdict;
    if (drift < 0.05) verdict = 'stable';
    else if (drift < 0.15) verdict = 'moderate';
    else verdict = 'unstable';
    return { pp, a, b, dLo, dHi, drift, verdict };
  });
  const verdictColor = (v) => ({
    stable: '#166737',
    moderate: '#B45309',
    unstable: '#B11F1F',
    'n/a': '#7A8E83',
  }[v]);

  return (
    <div
      style={{
        marginTop: 12,
        border: '1px dashed #C9C2B0',
        background: '#FAFAF6',
        borderRadius: 6,
        padding: 10,
      }}
      data-testid="gae-bootstrap-seed-compare-panel"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          fontSize: 12, fontWeight: 600, color: '#1F3A2E',
          background: 'transparent', border: 'none', cursor: 'pointer',
          padding: 0,
        }}
        data-testid="gae-bootstrap-seed-compare-toggle"
      >
        {open ? '▼' : '▶'} Compare two seeds (sampling-noise audit)
      </button>
      {open && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ fontSize: 11, color: '#3F4D44' }}>Seed A:&nbsp;
              <input
                type="number"
                value={seedA}
                onChange={(e) => setSeedA(parseInt(e.target.value, 10) || 0)}
                disabled={busy}
                style={{ width: 100, fontSize: 11, padding: '3px 5px', border: '1px solid #C8D4CD', borderRadius: 4, fontFamily: 'monospace' }}
                data-testid="gae-bootstrap-seed-compare-A"
              />
            </label>
            <label style={{ fontSize: 11, color: '#3F4D44' }}>Seed B:&nbsp;
              <input
                type="number"
                value={seedB}
                onChange={(e) => setSeedB(parseInt(e.target.value, 10) || 0)}
                disabled={busy}
                style={{ width: 100, fontSize: 11, padding: '3px 5px', border: '1px solid #C8D4CD', borderRadius: 4, fontFamily: 'monospace' }}
                data-testid="gae-bootstrap-seed-compare-B"
              />
            </label>
            <Button
              onClick={run}
              disabled={busy || seedA === seedB}
              style={{ background: '#2C5530', color: 'white', fontSize: 11, padding: '4px 10px' }}
              data-testid="gae-bootstrap-seed-compare-run"
            >
              {busy ? 'Running both seeds...' : 'Compare'}
            </Button>
            {seedA === seedB && (
              <span style={{ fontSize: 10, color: '#B11F1F', fontStyle: 'italic' }}>
                Seeds must differ.
              </span>
            )}
          </div>
          {err && (
            <div style={{ marginTop: 6, padding: 6, background: '#FBEAEA', color: '#B11F1F', fontSize: 11, borderRadius: 4 }}>
              {err}
            </div>
          )}
          {bootA && bootB && (
            <div style={{ marginTop: 8, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }} data-testid="gae-bootstrap-seed-compare-table">
                <thead>
                  <tr style={{ background: '#EFEAE0' }}>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Predictor (phenotypic path)</th>
                    <th style={{ padding: '4px 6px', textAlign: 'center' }}>Seed A CI</th>
                    <th style={{ padding: '4px 6px', textAlign: 'center' }}>Seed B CI</th>
                    <th style={{ padding: '4px 6px', textAlign: 'center' }}>|Δ lo|</th>
                    <th style={{ padding: '4px 6px', textAlign: 'center' }}>|Δ hi|</th>
                    <th style={{ padding: '4px 6px', textAlign: 'center' }}>Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ pp, a, b, dLo, dHi, verdict }) => (
                    <tr key={pp} style={{ borderBottom: '1px solid #E4E1D6' }} data-testid={`gae-bootstrap-seed-compare-row-${pp}`}>
                      <td style={{ padding: '3px 6px', fontWeight: 600 }}>{pp}</td>
                      <td style={{ padding: '3px 6px', textAlign: 'center', fontFamily: 'monospace' }}>
                        {a ? `[${a.lo.toFixed(3)}, ${a.hi.toFixed(3)}]` : '—'}
                      </td>
                      <td style={{ padding: '3px 6px', textAlign: 'center', fontFamily: 'monospace' }}>
                        {b ? `[${b.lo.toFixed(3)}, ${b.hi.toFixed(3)}]` : '—'}
                      </td>
                      <td style={{ padding: '3px 6px', textAlign: 'center', fontFamily: 'monospace' }}>
                        {dLo !== null ? Math.abs(dLo).toFixed(4) : '—'}
                      </td>
                      <td style={{ padding: '3px 6px', textAlign: 'center', fontFamily: 'monospace' }}>
                        {dHi !== null ? Math.abs(dHi).toFixed(4) : '—'}
                      </td>
                      <td style={{ padding: '3px 6px', textAlign: 'center', fontWeight: 600, color: verdictColor(verdict) }}>
                        {verdict}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 10, fontStyle: 'italic', color: '#6B7B72', marginTop: 6, lineHeight: 1.5 }}>
                <strong>Read:</strong> <span style={{ color: '#166737' }}>stable</span> = |Δ| {'<'} 5% of |est| (sampling-noise free, your B is large enough).{' '}
                <span style={{ color: '#B45309' }}>moderate</span> = 5-15%.{' '}
                <span style={{ color: '#B11F1F' }}>unstable</span> = {'>'} 15% (raise B to 500 or 1000; switch to email bundle for B=1000).
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const LOOCVPanel = ({ analysisId, onResult }) => {
  const [loocv, setLoocv] = React.useState(null);
  const [svg, setSvg] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const run = async () => {
    if (!analysisId) return;
    setBusy(true);
    setErr(null);
    setLoocv(null);
    setSvg(null);
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/loocv/${analysisId}`,
        { withCredentials: true, timeout: 60000 },
      );
      setLoocv(r.data);
      if (onResult) onResult(r.data);
      const s = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/loocv-tornado/${analysisId}.svg`,
        { responseType: 'text', withCredentials: true, timeout: 60000 },
      );
      setSvg(s.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'LOOCV failed');
    } finally {
      setBusy(false);
    }
  };

  const rankPill = (rank) => {
    const map = {
      stable: { bg: '#E8F4ED', fg: '#166737', border: '#B7DEC6' },
      moderate: { bg: '#FFF6E0', fg: '#B45309', border: '#E5C46B' },
      unstable: { bg: '#FBEAEA', fg: '#B11F1F', border: '#E04848' },
      'n/a': { bg: '#EFEAE0', fg: '#6B7B72', border: '#CCCCCC' },
    };
    const s = map[rank] || map['n/a'];
    return (
      <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                      background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}>
        {rank}
      </span>
    );
  };

  const predictors = loocv?.meta?.predictors || [];

  return (
    <div data-testid="gae-loocv-panel">
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
        <Button
          data-testid="gae-loocv-run"
          onClick={run}
          disabled={busy || !analysisId}
          style={{ background: '#2C5530', color: 'white' }}
        >
          {busy ? 'Running LOOCV...' : 'Run LOOCV (Stone 1974)'}
        </Button>
        {loocv && (
          <DownloadButton
            testId="gae-loocv-csv"
            url={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/loocv-csv/${analysisId}`}
            filename="loocv.csv"
            label="CSV (long format)"
            color="#4A7C59"
          />
        )}
        {loocv && (
          <DownloadButton
            testId="gae-loocv-svg"
            url={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/loocv-tornado/${analysisId}.svg`}
            filename="loocv_tornado.svg"
            label="SVG tornado plot"
            color="#4A7C59"
          />
        )}
      </div>
      {err && (
        <div data-testid="gae-loocv-err" style={{ padding: 8, background: '#FBEAEA', color: '#B11F1F', borderRadius: 4, fontSize: 12, marginBottom: 10 }}>{err}</div>
      )}
      {busy && (
        <div data-testid="gae-loocv-busy" style={{ padding: 8, background: '#E8F4ED', color: '#166737', borderRadius: 4, fontSize: 12 }}>
          Running {(predictors.length ? predictors.length : 'n')} refits... typically {'\u2248'}3-5 seconds.
        </div>
      )}
      {loocv && svg && (
        <div
          data-testid="gae-loocv-tornado"
          style={{ background: '#FFFFFF', borderRadius: 8, padding: 8, border: '1px solid #E4E1D6', marginTop: 8, marginBottom: 8, overflow: 'auto' }}
          dangerouslySetInnerHTML={{ __html: _sanitizeSvg(svg) }}
        />
      )}
      {loocv && <Caption>{loocv.caption}</Caption>}

      {loocv && (
        <>
          <h5 style={{ fontSize: 13, fontWeight: 600, color: '#1F3A2E', margin: '10px 0 4px' }}>
            I1. Per-Predictor Stability of Direct Effects
          </h5>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="gae-loocv-stability-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#EFEAE0' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>Predictor</th>
                  {['pearson','phenotypic','genotypic']
                    .filter((p) => loocv.stability?.[p])
                    .map((p) => (
                      <th key={p} style={{ padding: '5px 8px', textAlign: 'left', textTransform: 'capitalize' }} colSpan={2}>{p}</th>
                    ))}
                </tr>
                <tr style={{ background: '#EFEAE0' }}>
                  <th style={{ padding: '5px 8px' }} />
                  {['pearson','phenotypic','genotypic']
                    .filter((p) => loocv.stability?.[p])
                    .flatMap((p) => [
                      <th key={`${p}-est`} style={{ padding: '3px 6px', fontSize: 10, fontWeight: 500, color: '#5A6B62' }}>Full · LOO range</th>,
                      <th key={`${p}-rank`} style={{ padding: '3px 6px', fontSize: 10, fontWeight: 500, color: '#5A6B62' }}>Sign · Rank</th>,
                    ])}
                </tr>
              </thead>
              <tbody>
                {predictors.map((pp) => (
                  <tr key={pp} style={{ borderBottom: '1px solid #E4E1D6' }}>
                    <td style={{ padding: '5px 8px', fontWeight: 600 }}>{pp}</td>
                    {['pearson','phenotypic','genotypic']
                      .filter((p) => loocv.stability?.[p])
                      .flatMap((p) => {
                        const b = loocv.stability[p][pp] || {};
                        return [
                          <td key={`${p}-${pp}-est`} style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 11 }}>
                            {b.full_est >= 0 ? '+' : ''}{(b.full_est ?? 0).toFixed(3)}
                            {' '}
                            <span style={{ color: '#7A8E83' }}>
                              [{b.loo_min >= 0 ? '+' : ''}{(b.loo_min ?? 0).toFixed(3)}, {b.loo_max >= 0 ? '+' : ''}{(b.loo_max ?? 0).toFixed(3)}]
                            </span>
                          </td>,
                          <td key={`${p}-${pp}-rank`} style={{ padding: '4px 8px', fontSize: 11 }}>
                            <span style={{ color: '#3F4D44' }}>{b.sign_stability_pct ?? '—'}%</span>{' · '}
                            {rankPill(b.rank)}
                          </td>,
                        ];
                      })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h5 style={{ fontSize: 13, fontWeight: 600, color: '#1F3A2E', margin: '12px 0 4px' }}>
            I2. R² Stability + Cross-Validated R² (Pearson)
          </h5>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="gae-loocv-r2-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#EFEAE0' }}>
                  {['Path type','Full R²','LOO mean','LOO SD','Range','Rank'].map((h) => (
                    <th key={h} style={{ padding: '5px 8px', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(loocv.r_squared_stability || {}).map(([p, b]) => (
                  <tr key={p} style={{ borderBottom: '1px solid #E4E1D6' }}>
                    <td style={{ padding: '5px 8px', fontWeight: 600, textTransform: 'capitalize' }}>{p}</td>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{(b.full_est ?? 0).toFixed(3)}</td>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{(b.loo_mean ?? 0).toFixed(3)}</td>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{(b.loo_sd ?? 0).toFixed(3)}</td>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace', color: '#7A8E83' }}>[{(b.loo_min ?? 0).toFixed(3)}, {(b.loo_max ?? 0).toFixed(3)}]</td>
                    <td style={{ padding: '5px 8px' }}>{rankPill(b.rank)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {loocv.cv_r_squared_pearson && !loocv.cv_r_squared_pearson.error && (
            <Caption>
              <strong>Cross-validated R² (Pearson, standardized scale): {loocv.cv_r_squared_pearson.cv_r_squared}</strong>
              {' · '}PRESS = {loocv.cv_r_squared_pearson.press}
              {' · '}RMSEP = {loocv.cv_r_squared_pearson.rmsep}
              {' · '}n = {loocv.cv_r_squared_pearson.n_predictions}
              {'. Lower than the in-sample R² indicates expected predictive shrinkage; matches indicate strong generalisation across genotypes.'}
            </Caption>
          )}

          <h5 style={{ fontSize: 13, fontWeight: 600, color: '#1F3A2E', margin: '12px 0 4px' }}>
            I3. Top {loocv.meta.top_k} Most Influential Genotypes
          </h5>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="gae-loocv-influential-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#EFEAE0' }}>
                  {['Rank','Genotype','Leverage score','Most-affected predictor','|Δ direct effect|'].map((h) => (
                    <th key={h} style={{ padding: '5px 8px', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(loocv.influential_genotypes || []).map((lev, idx) => (
                  <tr key={lev.genotype} style={{ borderBottom: '1px solid #E4E1D6' }}>
                    <td style={{ padding: '5px 8px', fontWeight: 600 }}>{idx + 1}</td>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace', fontWeight: 600 }}>{lev.genotype}</td>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{lev.leverage_score}</td>
                    <td style={{ padding: '5px 8px' }}>{lev.most_affected_predictor || '—'}</td>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace', color: '#B45309' }}>{lev.most_affected_delta ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Caption>
            {'Genotypes ranked by Σ |Δ direct effect on Pearson path| when that genotype is held out. High leverage means the genotype substantially shifts the fitted path coefficients — review these genotypes for data-entry errors, exceptional traits, or unique biological behaviour before manuscript submission. This is a Cook\u2019s-distance-style diagnostic (Cook 1977) adapted to path analysis.'}
          </Caption>
        </>
      )}
    </div>
  );
};

// ─── P6: Robustness Consensus Card ────────────────────────────────
// Sub-Batch 3C+ enhancement (Niranjan spec Feb 2026).
// Triangulates Bootstrap (3A) + LOOCV (3B) + Sequential Path (3C)
// to produce a 3-row pill display:
//   GREEN  — all 3 methods retain (consensus reliable)
//   AMBER  — mixed evidence (1 or 2 of 3 retain)
//   RED    — all 3 methods eliminate (consensus unreliable)
// Each pill is clickable → jumps to Section H/I/J.
// Card greys out if any of the 3 methods has not yet been run.
//
// Union vs intersection discipline (PRD pattern, Feb 2026 visual-fix round II):
//   "retains" = ALL path types (Pearson + Phenotypic + Genotypic) support
//   "eliminates" = ALL path types eliminate
//   Anything in between = mixed for that single method
const _bootSupportsForPredictor = (boot, pp) => {
  // Bootstrap supports = ALL 3 path types' CIs for direct effect
  // on this predictor exclude zero (intersection across path types).
  // Eliminates = ALL 3 CIs include zero.
  if (!boot || !boot.paths) return 'unknown';
  const ptypes = ['pearson', 'phenotypic', 'genotypic'];
  let nSupport = 0;
  let nEliminate = 0;
  for (const pt of ptypes) {
    const d = boot.paths[pt]?.direct?.[pp];
    if (!d || d.lo == null || d.hi == null) continue;
    if ((d.lo > 0 && d.hi > 0) || (d.lo < 0 && d.hi < 0)) {
      nSupport += 1;
    } else {
      nEliminate += 1;
    }
  }
  if (nSupport === 3) return 'support';
  if (nEliminate === 3) return 'eliminate';
  return 'mixed';
};

const _loocvSupportsForPredictor = (loocv, pp) => {
  if (!loocv || !loocv.stability) return 'unknown';
  const ptypes = ['pearson', 'phenotypic', 'genotypic'];
  let nSupport = 0;
  let nEliminate = 0;
  for (const pt of ptypes) {
    const rank = loocv.stability[pt]?.[pp]?.rank;
    if (!rank || rank === 'n/a') continue;
    if (rank === 'stable' || rank === 'moderate') nSupport += 1;
    else if (rank === 'unstable') nEliminate += 1;
  }
  if (nSupport === 3) return 'support';
  if (nEliminate === 3) return 'eliminate';
  return 'mixed';
};

const _seqSupportsForPredictor = (seq, pp) => {
  // Sequential Path uses the already-computed consensus on the seq blob:
  //   retained_by_all = predictor retained by ALL 3 path types
  //   eliminated_by_all = predictor eliminated by ALL 3 path types
  if (!seq || !seq.consensus) return 'unknown';
  if (seq.consensus.retained_by_all?.includes(pp)) return 'support';
  if (seq.consensus.eliminated_by_all?.includes(pp)) return 'eliminate';
  return 'mixed';
};

const _classifyPredictor = (boot, loocv, seq, pp) => {
  const verdicts = [
    _bootSupportsForPredictor(boot, pp),
    _loocvSupportsForPredictor(loocv, pp),
    _seqSupportsForPredictor(seq, pp),
  ];
  const nSupport = verdicts.filter((v) => v === 'support').length;
  const nEliminate = verdicts.filter((v) => v === 'eliminate').length;
  if (nSupport === 3) return { bucket: 'green', verdicts };
  if (nEliminate === 3) return { bucket: 'red', verdicts };
  return { bucket: 'amber', verdicts };
};

const _scrollToAnchor = (anchorId) => {
  const el = document.getElementById(anchorId);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const RobustnessConsensusCard = ({ analysisId, bootData, loocvData, seqData }) => {
  const ready = Boolean(bootData && loocvData && seqData);
  const meta = seqData?.meta || bootData?.meta || loocvData?.meta || {};
  const predictors = meta.predictors || [];

  if (!ready) {
    return (
      <div
        data-testid="gae-robustness-consensus-card"
        style={{
          border: '2px dashed #C9C2B0',
          background: '#FAF8F2',
          borderRadius: 8,
          padding: 20,
          marginTop: 20,
          marginBottom: 20,
          color: '#6B7B72',
          fontSize: 13,
          textAlign: 'center',
        }}
      >
        <div style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 22, fontStyle: 'italic', color: '#2C5530', marginBottom: 8 }}>
          Statistical Robustness Consensus
        </div>
        <div data-testid="gae-robustness-consensus-empty">
          Run all 3 methods (Section H Bootstrap, Section I LOOCV, Section J Sequential Path) below to view consensus.
        </div>
        <div style={{ fontSize: 11, color: '#9CA098', marginTop: 6 }}>
          Triangulation principle: a predictor is fully trustworthy only when all 3 robustness methods agree.
        </div>
      </div>
    );
  }

  const classified = predictors.map((pp) => ({
    pp,
    ...(_classifyPredictor(bootData, loocvData, seqData, pp)),
  }));
  const green = classified.filter((c) => c.bucket === 'green');
  const amber = classified.filter((c) => c.bucket === 'amber');
  const red = classified.filter((c) => c.bucket === 'red');

  const verdictLabel = (v, method) => {
    if (v === 'support') return `${method}: all paths retain`;
    if (v === 'eliminate') return `${method}: all paths eliminate`;
    if (v === 'mixed') return `${method}: mixed across paths`;
    return `${method}: unknown`;
  };
  const tooltipFor = ({ verdicts }) => {
    const [b, l, s] = verdicts;
    return `${verdictLabel(b, 'Bootstrap')} | ${verdictLabel(l, 'LOOCV')} | ${verdictLabel(s, 'Sequential')}`;
  };

  const pillRow = (label, items, color, testId, anchor) => (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '10px 12px',
        background: color.bg,
        border: `1px solid ${color.border}`,
        borderRadius: 6,
        marginBottom: 8,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 240, color: color.text, fontWeight: 600, fontSize: 13 }}>
        {label} <span style={{ fontWeight: 400, color: '#6B7B72' }}>({items.length})</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, flex: 1 }}>
        {items.length === 0 ? (
          <span style={{ fontSize: 12, color: '#9CA098', fontStyle: 'italic' }}>(none)</span>
        ) : items.map((c) => (
          <button
            key={c.pp}
            type="button"
            data-testid={`gae-rcc-pill-${c.pp}`}
            onClick={() => _scrollToAnchor(anchor)}
            title={tooltipFor(c)}
            style={{
              padding: '3px 10px',
              background: 'white',
              color: color.text,
              border: `1px solid ${color.border}`,
              borderRadius: 99,
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {c.pp}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div
      data-testid="gae-robustness-consensus-card"
      style={{
        border: '1px solid #B7DEC6',
        background: '#FBFAF6',
        borderRadius: 8,
        padding: 16,
        marginTop: 20,
        marginBottom: 20,
      }}
    >
      <h2
        data-testid="gae-rcc-heading"
        style={{
          fontFamily: 'Cormorant Garamond, Georgia, serif',
          fontStyle: 'italic',
          fontSize: 24,
          color: '#2C5530',
          margin: '0 0 12px',
        }}
      >
        Statistical Robustness Consensus (Bootstrap + LOOCV + Sequential Path)
      </h2>

      {pillRow(
        'Consensus reliable — all 3 methods retain',
        green,
        { bg: '#E8F4ED', border: '#B7DEC6', text: '#166737' },
        'gae-rcc-green-row',
        'section-j',
      )}
      {pillRow(
        'Mixed evidence — methods disagree',
        amber,
        { bg: '#FFF6E0', border: '#E5C46B', text: '#B45309' },
        'gae-rcc-amber-row',
        'section-i',
      )}
      {pillRow(
        'Consensus unreliable — all 3 methods eliminate',
        red,
        { bg: '#FDE7E7', border: '#F2B5B5', text: '#B11F1F' },
        'gae-rcc-red-row',
        'section-h',
      )}

      <div
        data-testid="gae-rcc-footer"
        style={{
          fontSize: 11.5,
          fontStyle: 'italic',
          color: '#6B7B72',
          marginTop: 10,
          lineHeight: 1.55,
        }}
      >
        Triangulation principle: a predictor is fully trustworthy if Bootstrap CI excludes zero <em>and</em> LOOCV is stable <em>and</em> Sequential Path retains it. Amber predictors warrant case-by-case judgement. Reference: Whewell (1840) consilience of evidence.
      </div>
      <div
        data-testid="gae-rcc-provenance"
        style={{
          fontSize: 11,
          color: '#6B7B72',
          marginTop: 6,
          fontFamily: 'Menlo, Consolas, monospace',
        }}
      >
        Dataset: {meta.dataset_filename || '(unknown)'}
        {' \u00B7 '}N predictors = {predictors.length}
        {meta.dep_var ? ` \u00B7 Dependent variable: ${meta.dep_var}` : ''}
        {' \u00B7 '}Generated {new Date().toISOString().replace('T', ' ').slice(0, 16)} UTC
      </div>
      {/* Phase 4 #3 — Consensus Decision Brief PDF download (ICAR
          review-ready bundle of 3A + 3B + 3C + 3D in one PDF). */}
      <div
        data-testid="gae-rcc-decision-brief-row"
        style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <DownloadButton
          testId="gae-rcc-decision-brief-download"
          url={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/decision-brief/${analysisId}.pdf?B=200`}
          filename="decision_brief.pdf"
          label="Download Consensus Decision Brief (PDF)"
          color="#2C5530"
        />
        <span style={{ fontSize: 11, color: '#6B7B72', fontStyle: 'italic' }}>
          Bundles 3A + 3B + 3C + 3D + consensus table for ICAR/UAS reviewers (~3-5 pages).
        </span>
      </div>
    </div>
  );
};

const SequentialPathPanel = ({ analysisId, onResult }) => {
  const [threshold, setThreshold] = React.useState(0.10);
  const [seq, setSeq] = React.useState(null);
  const [svg, setSvg] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const run = async () => {
    if (!analysisId) return;
    setBusy(true);
    setErr(null);
    setSeq(null);
    setSvg(null);
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/sequential-path/${analysisId}`,
        { params: { threshold }, withCredentials: true, timeout: 30000 },
      );
      setSeq(r.data);
      if (onResult) onResult(r.data);
      const s = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/sequential-path-plot/${analysisId}.svg`,
        { params: { threshold }, responseType: 'text', withCredentials: true, timeout: 30000 },
      );
      setSvg(s.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Sequential path failed');
    } finally {
      setBusy(false);
    }
  };

  const predictors = seq?.meta?.predictors || [];

  return (
    <div data-testid="gae-seqpath-panel">
      {/* Reactive subtitle (line 2 of Section J heading per user spec Feb 2026) */}
      <div
        data-testid="gae-seqpath-subtitle"
        style={{
          fontSize: 13,
          color: '#4A7C59',
          fontStyle: 'italic',
          margin: '-4px 0 12px',
          lineHeight: 1.4,
        }}
      >
        {`Threshold |direct effect| \u2265 ${threshold.toFixed(2)} \u00B7 Singh-Chaudhary 1985 \u00A710 convention`}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#1F3A2E', whiteSpace: 'nowrap' }}>
          Threshold |direct effect|:
        </label>
        <input
          type="number"
          step="0.01"
          min="0.0"
          max="1.0"
          value={threshold}
          onChange={(e) => setThreshold(Math.max(0, Math.min(1, parseFloat(e.target.value) || 0.10)))}
          data-testid="gae-seqpath-threshold"
          disabled={busy}
          style={{ width: 70, padding: '4px 6px', fontSize: 12, border: '1px solid #C9C2B0', borderRadius: 4 }}
        />
        <span style={{ fontSize: 11, color: '#7A8E83', flex: '1 1 220px', minWidth: 180 }}>
          (Singh &amp; Chaudhary 1985 §10 convention: 0.10. Higher = more aggressive elimination.)
        </span>
        <Button
          data-testid="gae-seqpath-run"
          onClick={run}
          disabled={busy || !analysisId}
          style={{ background: '#2C5530', color: 'white', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {busy
            ? 'Running...'
            : `Run Sequential Path (\u2265 ${threshold.toFixed(2)})`}
        </Button>
        {seq && (
          <DownloadButton
            testId="gae-seqpath-csv"
            url={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/sequential-path-csv/${analysisId}?threshold=${threshold}`}
            filename={`sequential_path_t${threshold.toFixed(2)}.csv`}
            label="CSV"
            color="#4A7C59"
          />
        )}
        {seq && (
          <DownloadButton
            testId="gae-seqpath-svg"
            url={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/sequential-path-plot/${analysisId}.svg?threshold=${threshold}`}
            filename={`sequential_path_t${threshold.toFixed(2)}.svg`}
            label="SVG"
            color="#4A7C59"
          />
        )}
      </div>
      {err && (
        <div data-testid="gae-seqpath-err" style={{ padding: 8, background: '#FBEAEA', color: '#B11F1F', borderRadius: 4, fontSize: 12, marginBottom: 10 }}>{err}</div>
      )}
      {seq && (
        <>
          <div data-testid="gae-seqpath-consensus" style={{ padding: 10, background: '#E8F4ED', border: '1px solid #B7DEC6', borderRadius: 4, marginBottom: 10, fontSize: 12, color: '#166737' }}>
            <div data-testid="gae-seqpath-consensus-header" style={{ fontWeight: 600, marginBottom: 4, color: '#1F3A2E' }}>
              {`Consensus on ${seq.meta?.dataset_filename || '(unknown)'} (${seq.meta?.n_predictors_full ?? '?'} predictors${seq.meta?.dep_var ? ` -> ${seq.meta.dep_var}` : ''}) at threshold ${(seq.meta?.threshold ?? threshold).toFixed(2)}`}
            </div>
            <strong>Retained by all 3 path types ({seq.consensus?.n_consensus_retained ?? 0}):</strong>{' '}
            {seq.consensus?.retained_by_all?.length
              ? seq.consensus.retained_by_all.join(', ')
              : '(none)'}
            {' \u00B7 '}
            <strong>Eliminated by all 3 ({seq.consensus?.n_consensus_eliminated ?? 0}):</strong>{' '}
            {seq.consensus?.eliminated_by_all?.length
              ? seq.consensus.eliminated_by_all.join(', ')
              : '(none)'}
          </div>
          {svg && (
            <div
              data-testid="gae-seqpath-svg-inline"
              style={{ background: '#FFFFFF', borderRadius: 8, padding: 8, border: '1px solid #E4E1D6', marginBottom: 8, overflow: 'auto' }}
              dangerouslySetInnerHTML={{ __html: _sanitizeSvg(svg) }}
            />
          )}
          <Caption>{seq.caption}</Caption>

          <h5 style={{ fontSize: 13, fontWeight: 600, color: '#1F3A2E', margin: '12px 0 4px' }}>
            J1. Elimination Trace
          </h5>
          {['pearson', 'phenotypic', 'genotypic']
            .filter((p) => seq.per_path_type?.[p])
            .map((p) => {
              const blob = seq.per_path_type[p];
              if (!blob.trace?.length) {
                return (
                  <div key={p} style={{ fontSize: 12, color: '#5A6B62', marginBottom: 6 }}>
                    <strong style={{ textTransform: 'capitalize' }}>{p}:</strong>{' '}
                    No predictors below threshold — all retained.
                  </div>
                );
              }
              return (
                <div key={p} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1F3A2E', textTransform: 'capitalize', marginBottom: 4 }}>
                    {p} path — {blob.trace.length} elimination(s)
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table data-testid={`gae-seqpath-trace-${p}`} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#EFEAE0' }}>
                          {['Step','Dropped','Direct at drop','R² before','R² after','ΔR²','N after'].map((h) => (
                            <th key={h} style={{ padding: '4px 8px', textAlign: 'left' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {blob.trace.map((tr) => (
                          <tr key={tr.step} style={{ borderBottom: '1px solid #E4E1D6' }}>
                            <td style={{ padding: '4px 8px', fontWeight: 600 }}>{tr.step}</td>
                            <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: '#B11F1F' }}>{tr.dropped}</td>
                            <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{tr.dropped_direct_effect >= 0 ? '+' : ''}{tr.dropped_direct_effect}</td>
                            <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{tr.r_squared_before}</td>
                            <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{tr.r_squared_after}</td>
                            <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: tr.delta_r_squared < -0.01 ? '#B11F1F' : '#7A8E83' }}>
                              {tr.delta_r_squared >= 0 ? '+' : ''}{tr.delta_r_squared}
                            </td>
                            <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{tr.n_retained_after}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

          <h5 style={{ fontSize: 13, fontWeight: 600, color: '#1F3A2E', margin: '14px 0 4px' }}>
            J2. Full vs Lean Model Comparison
          </h5>
          {['pearson', 'phenotypic', 'genotypic']
            .filter((p) => seq.per_path_type?.[p]?.full_model?.r_squared_saturated || seq.per_path_type?.[p]?.lean_model?.r_squared_saturated)
            .map((p) => {
              const blob = seq.per_path_type[p];
              const raw = blob.full_model?.r_squared_raw;
              return (
                <div key={`sat-${p}`} data-testid={`gae-seqpath-saturation-${p}`} style={{
                  padding: 10, marginBottom: 8,
                  background: '#FBEAEA', border: '1px solid #E04848',
                  borderRadius: 4, color: '#B11F1F', fontSize: 12,
                }}>
                  <strong>R² saturated — {p} path</strong>: displayed R² clipped to 1.000.
                  {' Raw Σ(P·rg) = '}
                  <strong>{raw != null ? raw.toFixed(4) : '?'}</strong>
                  {' exceeds 1.0, indicating correlation-matrix inflation (Hayman 1958). '}
                  Typical cause: non-positive-definite genotypic correlation matrix or severe multicollinearity. Interpret direct effects with caution; the absolute value of R² is not interpretable when saturated.
                </div>
              );
            })}
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="gae-seqpath-compare-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#EFEAE0' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>Path type</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>Full R²</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>Lean R²</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>ΔR²</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>Predictors (full → lean)</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>Variance retained</th>
                </tr>
              </thead>
              <tbody>
                {['pearson', 'phenotypic', 'genotypic']
                  .filter((p) => seq.per_path_type?.[p])
                  .map((p) => {
                    const blob = seq.per_path_type[p];
                    const pars = blob.parsimony || {};
                    const sat = blob.full_model?.r_squared_saturated;
                    return (
                      <tr key={p} style={{ borderBottom: '1px solid #E4E1D6', background: sat ? '#FFF6E0' : 'transparent' }}>
                        <td style={{ padding: '5px 8px', fontWeight: 600, textTransform: 'capitalize' }}>
                          {p}
                          {sat && <span style={{ marginLeft: 6, fontSize: 10, color: '#B11F1F', fontWeight: 600 }}>⚠ saturated</span>}
                        </td>
                        <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>
                          {blob.full_model.r_squared}{sat && <span style={{ fontSize: 10, color: '#B11F1F' }}> (raw {blob.full_model.r_squared_raw?.toFixed(3)})</span>}
                        </td>
                        <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{blob.lean_model?.r_squared ?? '—'}</td>
                        <td style={{ padding: '5px 8px', fontFamily: 'monospace', color: pars.delta_r_squared_total < -0.05 ? '#B11F1F' : '#166737' }}>
                          {pars.delta_r_squared_total >= 0 ? '+' : ''}{pars.delta_r_squared_total}
                        </td>
                        <td style={{ padding: '5px 8px', fontSize: 11 }}>
                          {pars.n_predictors_full} → <strong>{pars.n_predictors_lean}</strong> ({pars.n_dropped} dropped)
                        </td>
                        <td style={{ padding: '5px 8px', fontFamily: 'monospace', color: '#166737' }}>
                          {pars.fraction_variance_retained != null ? `${(pars.fraction_variance_retained * 100).toFixed(1)}%` : '—'}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <Caption>
            {'Lean models typically lose minimal variance because eliminated predictors had small direct effects by construction. A negative ΔR² > 5% (red) indicates the threshold was too aggressive — consider lowering it. Predictors marked '}<strong style={{ color: '#166737' }}>*</strong>
            {' in the figure y-axis labels are retained in the chosen path type\u2019s lean model.'}
          </Caption>
        </>
      )}
    </div>
  );
};

// ─── Sub-Batch 3D — Smith-Hazel Selection Index Panel (Section K) ───
const _DEFAULT_WEIGHTS = {
  Biomass: 1.0, DaysMaturity: -0.5, PanicleWt: 2.0, PlantHeight: 0.3,
  TestWeight: 1.5, GrainNumber: 1.2, Yield: 2.0,
};

const _consensusBadge = (bucket) => {
  if (bucket === 'green') {
    return {
      symbol: '\u25CF', color: '#166737', bg: '#E8F4ED', border: '#B7DEC6',
      tooltip: 'Robustness consensus: GREEN — all 3 methods retain. Safe to weight.',
    };
  }
  if (bucket === 'amber') {
    return {
      symbol: '\u26A0', color: '#B45309', bg: '#FFF6E0', border: '#E5C46B',
      tooltip: 'Mixed robustness evidence — Bootstrap CI spans zero on at least one path-type or LOOCV unstable. Including in index may amplify selection uncertainty. Consider weight = 0 sensitivity check.',
    };
  }
  if (bucket === 'red') {
    return {
      symbol: '\u26D4', color: '#B11F1F', bg: '#FDE7E7', border: '#F2B5B5',
      tooltip: 'Unreliable per all 3 robustness methods. Selection on this trait is NOT recommended. Strongly suggest weight = 0.',
    };
  }
  return null;
};

const SmithHazelPanel = ({ analysisId, classification, traitsHint }) => {
  // traitsHint: list of trait names from upstream (variance components)
  // classification: {trait: 'green'|'amber'|'red'} from consensus card
  const traits = React.useMemo(() => traitsHint || [], [traitsHint]);
  const [weights, setWeights] = React.useState(() => {
    const w = {};
    for (const t of traits) w[t] = _DEFAULT_WEIGHTS[t] !== undefined ? _DEFAULT_WEIGHTS[t] : 1.0;
    return w;
  });
  React.useEffect(() => {
    setWeights((prev) => {
      const next = { ...prev };
      for (const t of traits) {
        if (next[t] === undefined) {
          next[t] = _DEFAULT_WEIGHTS[t] !== undefined ? _DEFAULT_WEIGHTS[t] : 1.0;
        }
      }
      return next;
    });
  }, [traits]);
  const [si, setSi] = React.useState(null);
  const [svg, setSvg] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const run = async () => {
    if (!analysisId) return;
    setBusy(true); setErr(null); setSi(null); setSvg(null);
    try {
      const wParams = {};
      for (const [k, v] of Object.entries(weights || {})) {
        wParams[`w_${k}`] = v;
      }
      const cParams = {};
      for (const [k, v] of Object.entries(classification || {})) {
        if (v) cParams[`c_${k}`] = v;
      }
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/smith-hazel/${analysisId}`,
        { params: { ...wParams }, withCredentials: true, timeout: 30000 },
      );
      setSi(r.data);
      const s = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/smith-hazel-plot/${analysisId}.svg`,
        { params: { ...wParams, ...cParams }, responseType: 'text', withCredentials: true, timeout: 30000 },
      );
      setSvg(s.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Smith-Hazel failed');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="gae-smithhazel-panel">
      <div
        data-testid="gae-smithhazel-subtitle"
        style={{
          fontSize: 13,
          color: '#4A7C59',
          fontStyle: 'italic',
          margin: '-4px 0 12px',
          lineHeight: 1.4,
        }}
      >
        Multivariate selection index b = P{'\u207B\u00B9'} G w (Smith 1936; Hazel 1943) {'\u00B7'} Robustness-consensus badges from Section H + I + J flag traits with mixed or unreliable evidence.
      </div>

      {/* Editable weight inputs grid */}
      <div
        data-testid="gae-smithhazel-weights-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 10,
          padding: 10,
          background: '#FAF8F2',
          border: '1px solid #E4E1D6',
          borderRadius: 6,
          marginBottom: 10,
        }}
      >
        {traits.map((t) => {
          const bucket = (classification || {})[t];
          const badge = _consensusBadge(bucket);
          return (
            <div
              key={t}
              data-testid={`gae-smithhazel-weight-row-${t}`}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {badge && (
                <span
                  data-testid={`gae-smithhazel-badge-${t}`}
                  title={badge.tooltip}
                  style={{
                    background: badge.bg,
                    color: badge.color,
                    border: `1px solid ${badge.border}`,
                    borderRadius: 4,
                    padding: '0 5px',
                    fontSize: 12,
                    cursor: 'help',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {badge.symbol}
                </span>
              )}
              <label style={{ fontSize: 12, fontWeight: 600, color: '#1F3A2E', flex: '0 1 auto', minWidth: 60 }}>
                {t}:
              </label>
              <input
                type="number"
                step="0.1"
                value={weights[t] !== undefined ? weights[t] : 1.0}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  setWeights((prev) => ({ ...prev, [t]: Number.isFinite(v) ? v : 0 }));
                }}
                data-testid={`gae-smithhazel-weight-${t}`}
                disabled={busy}
                style={{ width: 70, padding: '3px 6px', fontSize: 12, border: '1px solid #C9C2B0', borderRadius: 4 }}
              />
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <Button
          data-testid="gae-smithhazel-run"
          onClick={run}
          disabled={busy || !analysisId}
          style={{ background: '#2C5530', color: 'white', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {busy ? 'Running...' : 'Run Selection Index (Smith 1936)'}
        </Button>
        {si && (
          <>
            <a
              data-testid="gae-smithhazel-csv"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/smith-hazel-csv/${analysisId}?${Object.entries(weights).map(([k, v]) => `w_${k}=${v}`).join('&')}`}
              style={{ fontSize: 12, color: '#2C5530', textDecoration: 'underline' }}
            >
              Download index CSV
            </a>
            <a
              data-testid="gae-smithhazel-svg-download"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/smith-hazel-plot/${analysisId}.svg?${Object.entries(weights).map(([k, v]) => `w_${k}=${v}`).join('&')}${Object.entries(classification || {}).filter(([, v]) => v).map(([k, v]) => `&c_${k}=${v}`).join('')}`}
              style={{ fontSize: 12, color: '#2C5530', textDecoration: 'underline' }}
            >
              Download SVG
            </a>
          </>
        )}
      </div>

      {err && (
        <div data-testid="gae-smithhazel-err" style={{ padding: 8, background: '#FBEAEA', color: '#B11F1F', borderRadius: 4, fontSize: 12, marginBottom: 10 }}>{err}</div>
      )}

      {si && si.regularization?.applied && (
        <div data-testid="gae-smithhazel-ridge-banner" style={{ padding: 10, background: '#FDE7E7', border: '1px solid #F2B5B5', borderRadius: 4, color: '#B11F1F', fontSize: 12, marginBottom: 10 }}>
          {'\u26A0 '}<strong>Ridge regularization applied</strong>
          {' \u2014 '}
          {si.regularization.P_was_non_pd && (
            <>
              {'P non-PD ('}{'\u03BB'}<sub>P</sub>{' = '}{si.regularization.lambda_P.toExponential(3)}{')'}
              {si.regularization.G_was_non_pd ? ' \u00B7 ' : ''}
            </>
          )}
          {si.regularization.G_was_non_pd && (
            <>
              {'G non-PD ('}{'\u03BB'}<sub>G</sub>{' = '}{si.regularization.lambda_G.toExponential(3)}{')'}
            </>
          )}
          {'. Index coefficients are approximate. Reference: Hayman 1958 on non-PD V-COV matrix inflation; Hoerl-Kennard 1970 on ridge regression.'}
        </div>
      )}

      {si && si.genetic_gain?.rho_IH_saturated && (
        <div data-testid="gae-smithhazel-rho-saturation" style={{ padding: 8, background: '#FFF6E0', border: '1px solid #E5C46B', borderRadius: 4, color: '#8B5A0A', fontSize: 11, marginBottom: 10 }}>
          {'\u24D8 Numerical inflation: raw \u03C1\u2099(I,H) = '}{si.genetic_gain.rho_IH_genetic_raw.toFixed(4)}{' exceeded 1 due to ridge regularization; clamped to '}{si.genetic_gain.rho_IH_genetic.toFixed(4)}{' for honest display.'}
        </div>
      )}

      {svg && (
        <div
          data-testid="gae-smithhazel-svg"
          style={{ overflow: 'auto', maxWidth: '100%', marginBottom: 12 }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: _sanitizeSvg(svg) }}
        />
      )}

      {si && (
        <>
          {/* Genetic-gain + RE summary */}
          <div
            data-testid="gae-smithhazel-gain-summary"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 10,
              padding: 10,
              background: '#FAF8F2',
              border: '1px solid #C9C2B0',
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 12,
              color: '#1F3A2E',
            }}
          >
            <div>
              <div style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontStyle: 'italic', color: '#2C5530', fontSize: 13 }}>
                Genetic SD of Index ({'\u03C3'}<sub>I,g</sub>)
              </div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {si.genetic_gain.sigma_I_genetic.toFixed(3)}
              </div>
              <div style={{ fontSize: 10, color: '#6B7B72' }}>
                {'\u0394'}G = i {'\u00D7'} {'\u03C3'}<sub>I,g</sub> = {si.genetic_gain.delta_G_index.toFixed(3)} per unit i
              </div>
            </div>
            <div>
              <div style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontStyle: 'italic', color: '#2C5530', fontSize: 13 }}>
                Index-Aggregate Correlation ({'\u03C1'}<sub>g</sub>(I,H))
              </div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {si.genetic_gain.rho_IH_genetic.toFixed(4)}
              </div>
              <div style={{ fontSize: 10, color: '#6B7B72' }}>
                b{'\u2032'}Gw / {'\u221A'}(b{'\u2032'}Gb {'\u00D7'} w{'\u2032'}Gw) — upper bound is 1
              </div>
            </div>
            <div>
              <div style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontStyle: 'italic', color: '#2C5530', fontSize: 13 }}>
                Relative Efficiency
              </div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {si.genetic_gain.RE_vs_baseline !== null ? si.genetic_gain.RE_vs_baseline.toFixed(3) : 'n/a'}{'\u00D7'}
              </div>
              <div style={{ fontSize: 10, color: '#6B7B72' }}>
                vs single-trait selection on {si.meta.baseline_trait}
              </div>
            </div>
          </div>

          {/* Top-10 ranking table */}
          <div data-testid="gae-smithhazel-ranking" style={{ overflowX: 'auto', marginBottom: 10 }}>
            <h4 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontStyle: 'italic', color: '#2C5530', margin: '0 0 6px', fontSize: 16 }}>
              Top-10 genotypes by index value
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#E4E1D6', color: '#1F3A2E' }}>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Rank</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left' }}>Genotype</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Index value I</th>
                  {si.meta.traits.map((t) => (
                    <th key={t} style={{ padding: '4px 8px', textAlign: 'right' }}>
                      {t}
                      {classification && _consensusBadge(classification[t]) && (
                        <span style={{ marginLeft: 4, color: _consensusBadge(classification[t]).color }}>
                          {_consensusBadge(classification[t]).symbol}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {si.ranking.slice(0, 10).map((row) => (
                  <tr key={row.genotype} style={{ borderBottom: '1px solid #E4E1D6' }}>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>{row.rank}</td>
                    <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{row.genotype}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#166737' }}>{row.index_value.toFixed(3)}</td>
                    {si.meta.traits.map((t) => (
                      <td key={t} style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace' }}>
                        {row[t] !== undefined && row[t] !== null ? row[t].toFixed(3) : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Caption>
            Smith-Hazel multivariate selection index (Smith 1936, J. Genet. 28; Hazel 1943, Genetics 28). Index coefficients b solve b = P{'\u207B\u00B9'} G w on the entry-mean basis (Singh &amp; Chaudhary 1985 §11). Robustness badges on weight inputs reflect the Sub-Batch 3C+ Consensus Card classification: green = all 3 methods retain, amber = mixed evidence, red = all 3 methods eliminate. Best practice: set red-flagged traits to weight 0 unless domain knowledge strongly justifies otherwise.
          </Caption>

          {/* Sensitivity panel inlined below — shows up only if classification has AMBER traits */}
          <SensitivityPanel
            analysisId={analysisId}
            classification={classification}
            traitsHint={traits}
            currentSi={si}
          />
        </>
      )}
    </div>
  );
};

// ─── Sub-Batch 3E — Cluster Stability Panel (Section L) ─────────────
const ClusterStabilityPanel = ({ analysisId }) => {
  const [stab, setStab] = React.useState(null);
  const [svg, setSvg] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const run = async () => {
    if (!analysisId) return;
    setBusy(true); setErr(null); setStab(null); setSvg(null);
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/cluster-stability/${analysisId}`,
        { withCredentials: true, timeout: 60000 },
      );
      setStab(r.data);
      const s = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/cluster-stability-plot/${analysisId}.svg`,
        { responseType: 'text', withCredentials: true, timeout: 60000 },
      );
      setSvg(s.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Cluster stability failed');
    } finally { setBusy(false); }
  };

  const tierColor = (pct) => {
    if (pct >= 70) return { bg: '#E8F4ED', border: '#B7DEC6', text: '#166737' };
    if (pct >= 40) return { bg: '#FFF6E0', border: '#E5C46B', text: '#B45309' };
    return { bg: '#FDE7E7', border: '#F2B5B5', text: '#B11F1F' };
  };

  return (
    <div data-testid="gae-clusterstab-panel">
      <div
        data-testid="gae-clusterstab-subtitle"
        style={{
          fontSize: 13,
          color: '#4A7C59',
          fontStyle: 'italic',
          margin: '-4px 0 12px',
          lineHeight: 1.4,
        }}
      >
        {'UPGMA on Mahalanobis D\u00B2 vs Ward on standardized Euclidean across percentile grid 25% \u2192 95% \u00B7 Agreement = same cluster count (intersection criterion, Pattern 9) \u00B7 References: Milligan & Cooper 1985; Hubert & Arabie 1985.'}
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
        <Button
          data-testid="gae-clusterstab-run"
          onClick={run}
          disabled={busy || !analysisId}
          style={{ background: '#2C5530', color: 'white', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {busy ? 'Running...' : 'Run Cluster Stability Scan'}
        </Button>
      </div>
      {err && (
        <div data-testid="gae-clusterstab-err" style={{ padding: 8, background: '#FBEAEA', color: '#B11F1F', borderRadius: 4, fontSize: 12, marginBottom: 10 }}>{err}</div>
      )}
      {stab && (() => {
        const tier = tierColor(stab.summary.agreement_pct);
        return (
          <div
            data-testid="gae-clusterstab-summary"
            style={{
              padding: 10,
              background: tier.bg,
              border: `1px solid ${tier.border}`,
              borderRadius: 4,
              marginBottom: 10,
              fontSize: 12,
              color: tier.text,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {`Agreement = ${stab.summary.agreement_pct.toFixed(1)}% (${stab.summary.n_agree}/${stab.summary.n_percentiles_tested} percentiles tested) \u00B7 Longest run: ${stab.summary.longest_agreement_run} consecutive percentiles at k = ${stab.summary.longest_run_k}`}
            </div>
            <div style={{ fontStyle: 'italic' }}>{stab.summary.recommendation}</div>
          </div>
        );
      })()}
      {svg && (
        <div
          data-testid="gae-clusterstab-svg"
          style={{ overflow: 'auto', maxWidth: '100%', marginBottom: 12 }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: _sanitizeSvg(svg) }}
        />
      )}
      {stab && (
        <div data-testid="gae-clusterstab-table" style={{ overflowX: 'auto', marginBottom: 10 }}>
          <h4 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontStyle: 'italic', color: '#2C5530', margin: '0 0 6px', fontSize: 16 }}>
            Per-percentile cluster-count table
          </h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#E4E1D6', color: '#1F3A2E' }}>
                <th style={{ padding: '4px 8px', textAlign: 'right' }}>Percentile</th>
                <th style={{ padding: '4px 8px', textAlign: 'right' }}>{'UPGMA threshold (D\u00B2)'}</th>
                <th style={{ padding: '4px 8px', textAlign: 'right' }}>Ward threshold (Eucl.)</th>
                <th style={{ padding: '4px 8px', textAlign: 'right' }}>k UPGMA</th>
                <th style={{ padding: '4px 8px', textAlign: 'right' }}>k Ward</th>
                <th style={{ padding: '4px 8px', textAlign: 'center' }}>Agree?</th>
              </tr>
            </thead>
            <tbody>
              {stab.stability_table.map((row) => (
                <tr key={row.percentile} style={{ borderBottom: '1px solid #E4E1D6', background: row.agree ? '#F0F8F2' : 'white' }}>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{row.percentile}%</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{row.threshold_upgma_D2.toFixed(2)}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{row.threshold_ward_euclidean.toFixed(2)}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#2C5530' }}>{row.k_upgma}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#B45309' }}>{row.k_ward}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                    {row.agree ? <span style={{ color: '#166737' }}>{'\u2713'}</span> : <span style={{ color: '#B11F1F' }}>{'\u2717'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {stab && (
        <Caption>
          Cluster stability across distance-threshold percentiles. UPGMA (Mahalanobis D{'\u00B2'}, Sneath-Sokal 1973) and Ward (standardized Euclidean, Ward 1963) cluster counts plotted on a common percentile x-axis. Green agreement strips mark percentiles where both methods produce the same cluster count (intersection criterion per PRD Pattern 9). Pink disagreement strips highlight method-sensitive partitions. Per Pattern 10 (uncertainty-honest decision aids), the recommendation surfaces the full sensitivity to threshold choice rather than naming a single "true" k.
        </Caption>
      )}
    </div>
  );
};

// ─── Sub-Batch 3F — Email-the-bundle Panel (Section M) ──────────────
const EmailBundlePanel = ({ analysisId, defaultEmail }) => {
  const [email, setEmail] = React.useState(defaultEmail || '');
  React.useEffect(() => { if (defaultEmail) setEmail(defaultEmail); }, [defaultEmail]);
  const [busy, setBusy] = React.useState(false);
  const [info, setInfo] = React.useState(null);
  const [err, setErr] = React.useState(null);

  const send = async () => {
    if (!analysisId || !email) return;
    setBusy(true); setErr(null); setInfo(null);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/email-bundle/${analysisId}`,
        { recipient_email: email },
        { withCredentials: true, timeout: 180000 },
      );
      setInfo(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Email bundle failed');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="gae-emailbundle-panel">
      <div
        data-testid="gae-emailbundle-subtitle"
        style={{
          fontSize: 13, color: '#4A7C59', fontStyle: 'italic',
          margin: '-4px 0 12px', lineHeight: 1.4,
        }}
      >
        {'Receive the publication bundle (Sections H + I + J + K + L: SVGs + PNGs + Smith-Hazel CSV) as a single ZIP delivered to your inbox via Resend. Bundle regenerates fresh on every send so timestamps + provenance footers reflect the moment of delivery.'}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#1F3A2E', whiteSpace: 'nowrap' }}>
          Recipient email:
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="gae-emailbundle-recipient"
          placeholder="you@example.org"
          disabled={busy}
          style={{ width: 280, padding: '6px 8px', fontSize: 12, border: '1px solid #C9C2B0', borderRadius: 4, flex: '1 1 200px', maxWidth: 320 }}
        />
        <Button
          data-testid="gae-emailbundle-send"
          onClick={send}
          disabled={busy || !analysisId || !email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
          style={{ background: '#2C5530', color: 'white', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {busy ? 'Building + sending bundle...' : 'Email me the bundle'}
        </Button>
      </div>
      {err && (
        <div data-testid="gae-emailbundle-err" style={{ padding: 10, background: '#FBEAEA', color: '#B11F1F', borderRadius: 4, fontSize: 12, marginBottom: 10 }}>
          {err}
          {err.includes('rate') && (
            <div style={{ fontSize: 11, marginTop: 6, fontStyle: 'italic' }}>
              {'Resend free tier: 100 emails/day, 3000/month. Wait until tomorrow or upgrade tier.'}
            </div>
          )}
        </div>
      )}
      {info && (
        <div data-testid="gae-emailbundle-success" style={{ padding: 10, background: '#E8F4ED', border: '1px solid #B7DEC6', borderRadius: 4, fontSize: 12, color: '#166737', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {'\u2713 Bundle sent successfully'}
          </div>
          <div>{`File: ${info.zip_filename}  \u00B7  Size: ${(info.bundle_size_bytes / 1024 / 1024).toFixed(2)} MB  \u00B7  Email ID: ${info.email_id}`}</div>
          <div style={{ marginTop: 8, fontSize: 11, color: '#1F3A2E' }}>
            <strong>Sections included:</strong>
            <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
              {info.sections.map((s) => (
                <li key={s.section} style={{ color: s.status === 'ok' ? '#166737' : '#B45309' }}>
                  <strong>{s.section}.</strong> {s.title} {s.status === 'ok' ? `\u2014 ${s.note}` : `\u2014 ${s.status}: ${s.note}`}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <Caption>
        Email delivery via Resend SDK 2.x; bundle generated on-demand from the analysis stored on this account. Resend free-tier limits: 100 emails/day, 3000/month, max 40 MB attachment. Provenance: every figure in the bundle carries the dataset filename + UTC timestamp + N predictors footer (Pattern 8). Reference: Resend transactional email API.
      </Caption>
    </div>
  );
};

// ─── Post-3F: Sensitivity panel + Data-Collection ROI hint ──────────
// Computes the "What if I drop AMBER traits?" comparison and the
// conservative-framed sample-size ROI estimate. Pattern 10 enforced:
// all panel-size figures shown as ranges with conservative-end pick.
const SensitivityPanel = ({ analysisId, classification, traitsHint, currentSi }) => {
  // amberTraits and currentSi (with full-weight si) come from upstream
  const amberTraits = React.useMemo(() => {
    return Object.entries(classification || {})
      .filter(([, b]) => b === 'amber')
      .map(([t]) => t);
  }, [classification]);
  const [busy, setBusy] = React.useState(false);
  const [excludedSi, setExcludedSi] = React.useState(null);
  const [err, setErr] = React.useState(null);

  const run = async () => {
    if (!analysisId || amberTraits.length === 0) return;
    setBusy(true); setErr(null); setExcludedSi(null);
    try {
      // Build weight vector with current weights but AMBER → 0
      const weights = {};
      const traits = (currentSi?.meta?.traits) || traitsHint || [];
      const fullWeights = currentSi?.meta?.weights || {};
      for (const t of traits) {
        weights[t] = amberTraits.includes(t) ? 0 : (fullWeights[t] ?? 1.0);
      }
      const params = {};
      for (const [k, v] of Object.entries(weights)) {
        params[`w_${k}`] = v;
      }
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/smith-hazel/${analysisId}`,
        { params, withCredentials: true, timeout: 30000 },
      );
      setExcludedSi(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Sensitivity computation failed');
    } finally { setBusy(false); }
  };

  if (!amberTraits.length || !currentSi) return null;

  const fullRE = currentSi.genetic_gain?.RE_vs_baseline;
  const altRE = excludedSi?.genetic_gain?.RE_vs_baseline;
  const deltaRE_pct = (fullRE && altRE) ? ((fullRE - altRE) / fullRE) * 100 : null;
  let recommendation = null;
  let recColor = null;
  if (deltaRE_pct !== null) {
    if (deltaRE_pct < 5) {
      recommendation = 'Low cost of caution — AMBER exclusion recommended.';
      recColor = { bg: '#E8F4ED', border: '#B7DEC6', text: '#166737' };
    } else if (deltaRE_pct < 15) {
      recommendation = 'Moderate trade-off — consider sample-size increase to resolve AMBER traits.';
      recColor = { bg: '#FFF6E0', border: '#E5C46B', text: '#B45309' };
    } else {
      recommendation = 'High efficiency loss — AMBER traits are critical; investigate further before excluding.';
      recColor = { bg: '#FDE7E7', border: '#F2B5B5', text: '#B11F1F' };
    }
  }

  // Data-Collection ROI hint (Pattern 10: ranges with conservative-end pick)
  // Bootstrap CI width ∝ 1/sqrt(n); to halve a CI need ~4x n. We give
  // a range: low end = 2x current sample, high end = 4x current sample
  // (the conservative target). User's spec mandates ranges, not points.
  const n_current = currentSi?.meta?.n_genotypes || null;
  const ampleAmber = amberTraits.length >= 2;

  return (
    <div data-testid="gae-sensitivity-panel" style={{
      border: '1px dashed #C9C2B0', background: '#FAF8F2',
      borderRadius: 8, padding: 14, marginTop: 20, marginBottom: 12,
    }}>
      <h3 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontStyle: 'italic', color: '#2C5530', margin: '0 0 6px', fontSize: 20 }}>
        {'What if I drop AMBER traits? — sensitivity panel'}
      </h3>
      <div style={{ fontSize: 12, color: '#4A7C59', fontStyle: 'italic', marginBottom: 12 }}>
        {`Auto-excludes the ${amberTraits.length} AMBER trait${amberTraits.length > 1 ? 's' : ''} (${amberTraits.join(', ')}) from the index and shows the ΔRE penalty. References: Hoerl-Kennard 1970; Whewell 1840.`}
      </div>
      <Button
        data-testid="gae-sensitivity-run"
        onClick={run}
        disabled={busy}
        style={{ background: '#2C5530', color: 'white', whiteSpace: 'nowrap', marginBottom: 10 }}
      >
        {busy ? 'Computing alternative scenario...' : 'Run AMBER-excluded scenario'}
      </Button>
      {err && (
        <div data-testid="gae-sensitivity-err" style={{ padding: 8, background: '#FBEAEA', color: '#B11F1F', borderRadius: 4, fontSize: 12, marginTop: 8 }}>{err}</div>
      )}
      {excludedSi && (
        <div data-testid="gae-sensitivity-results">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 10 }}>
            <div style={{ padding: 10, background: 'white', border: '1px solid #C9C2B0', borderRadius: 6 }}>
              <h4 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontStyle: 'italic', color: '#2C5530', margin: '0 0 4px', fontSize: 16 }}>
                {'Full weights (current)'}
              </h4>
              <div style={{ fontSize: 12, color: '#1F3A2E' }}>
                <div>{'\u03C3'}<sub>I,g</sub>{' = '}<strong>{currentSi.genetic_gain.sigma_I_genetic.toFixed(3)}</strong></div>
                <div>{'\u03C1'}<sub>g</sub>(I,H){' = '}<strong>{currentSi.genetic_gain.rho_IH_genetic.toFixed(4)}</strong></div>
                <div>RE{' = '}<strong>{fullRE !== null ? `${fullRE.toFixed(3)}\u00D7` : 'n/a'}</strong></div>
              </div>
            </div>
            <div style={{ padding: 10, background: '#FAF6E8', border: '1px solid #E5C46B', borderRadius: 6 }}>
              <h4 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontStyle: 'italic', color: '#B45309', margin: '0 0 4px', fontSize: 16 }}>
                {`AMBER excluded (${amberTraits.join(', ')} → 0)`}
              </h4>
              <div style={{ fontSize: 12, color: '#1F3A2E' }}>
                <div>{'\u03C3'}<sub>I,g</sub>{' = '}<strong>{excludedSi.genetic_gain.sigma_I_genetic.toFixed(3)}</strong></div>
                <div>{'\u03C1'}<sub>g</sub>(I,H){' = '}<strong>{excludedSi.genetic_gain.rho_IH_genetic.toFixed(4)}</strong></div>
                <div>RE{' = '}<strong>{altRE !== null ? `${altRE.toFixed(3)}\u00D7` : 'n/a'}</strong></div>
              </div>
            </div>
          </div>

          {deltaRE_pct !== null && recommendation && (
            <div
              data-testid="gae-sensitivity-recommendation"
              style={{
                marginTop: 12, padding: 12,
                background: recColor.bg,
                border: `1px solid ${recColor.border}`,
                borderRadius: 6, color: recColor.text, fontSize: 13,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {`\u0394RE = ${deltaRE_pct.toFixed(2)}% efficiency loss if AMBER traits excluded`}
              </div>
              <div>{recommendation}</div>
            </div>
          )}

          {/* Data-Collection ROI hint (Pattern 10: range, not point) */}
          {ampleAmber && n_current && (
            <div
              data-testid="gae-sensitivity-roi"
              style={{
                marginTop: 12, padding: 12,
                background: '#FAFAF5', border: '1px solid #C9C2B0',
                borderRadius: 6, fontSize: 12, color: '#1F3A2E',
              }}
            >
              <h4 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontStyle: 'italic', color: '#2C5530', margin: '0 0 6px', fontSize: 15 }}>
                Data-collection ROI hint
              </h4>
              {amberTraits.map((t) => {
                // Bootstrap CI width ∝ 1/sqrt(n). To shrink CI by ~50%
                // (typical threshold to push AMBER → GREEN), n must ≈ 4×.
                // Conservative range: 2× current (low) to 4× (high).
                const n_lo = Math.ceil(n_current * 1.0);
                const n_hi = Math.ceil(n_current * 3.0);
                const plot_years_hi = n_hi * 3;
                return (
                  <div key={t} style={{ marginBottom: 6 }}>
                    {`Estimated additional genotypes to resolve `}<strong>{t}</strong>{` to GREEN: `}
                    <strong style={{ color: '#B45309' }}>{`${n_lo}-${n_hi} additional`}</strong>
                    {` (range reflects uncertainty in current estimates). Conservative budget: plan for `}
                    <strong>{`${n_hi} genotypes × 3 reps = ${plot_years_hi} plot-years.`}</strong>
                  </div>
                );
              })}
              <div style={{ fontSize: 11, color: '#6B7B72', fontStyle: 'italic', marginTop: 6 }}>
                Reference: Efron-Tibshirani 1993 §10.5 on bootstrap CI width convergence (CI width ∝ 1/{'\u221A'}n). Ranges shown per PRD Pattern 10 (uncertainty-honest decision aids).
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const GeneralAnalysisEnhancedView = ({ data, analysisId }) => {
  const [mantelData, setMantelData] = React.useState(null);
  // Lifted state from BootstrapPanel / LOOCVPanel / SequentialPathPanel
  // so the RobustnessConsensusCard can aggregate across all 3 methods.
  // Each panel calls onResult({...}) after a successful fetch.
  const [bootData, setBootData] = React.useState(null);
  const [loocvData, setLoocvData] = React.useState(null);
  const [seqData, setSeqData] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    if (!analysisId) return undefined;
    axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/mantel/${analysisId}`,
      { withCredentials: true },
    ).then((r) => { if (!cancelled) setMantelData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [analysisId]);

  const [pubBundleConfig, setPubBundleConfig] = React.useState({
    dpi: 600,
    naming_convention: 'crop_science',
    include_captions: true,
    open: false,
    busy: false,
    err: null,
  });

  if (!data || data.error) {
    return (
      <Card style={{ padding: 16, background: '#FFF4E0', borderColor: '#F4B21A' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#B45309', fontWeight: 600 }}>
          <Info size={18} />
          {data?.error || 'No General Analysis Enhanced data available.'}
        </div>
      </Card>
    );
  }

  const meta = data.meta || {};
  const downloadCSVBundle = async () => {
    if (!analysisId) return;
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/export-csv-bundle/${analysisId}`,
        { responseType: 'blob', withCredentials: true },
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `BKAgriD_GeneralAnalysisPhase1_${analysisId.slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // noop — user-facing error reported by axios interceptor
    }
  };
  const downloadPublicationBundle = async () => {
    if (!analysisId) return;
    setPubBundleConfig((c) => ({ ...c, busy: true, err: null }));
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-analysis/publication-bundle/${analysisId}`,
        {
          params: {
            dpi: pubBundleConfig.dpi,
            naming_convention: pubBundleConfig.naming_convention,
            include_captions: pubBundleConfig.include_captions,
          },
          responseType: 'blob',
          withCredentials: true,
          timeout: 180000,
        },
      );
      const dispo = r.headers?.['content-disposition'] || '';
      const m = dispo.match(/filename="?([^"\n;]+)"?/);
      const filename = m?.[1] || `BKAgriD_PublicationFigures_${analysisId.slice(0, 8)}.zip`;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setPubBundleConfig((c) => ({ ...c, busy: false, open: false }));
    } catch (e) {
      setPubBundleConfig((c) => ({
        ...c, busy: false,
        err: e?.response?.data?.detail || 'Bundle generation failed',
      }));
    }
  };

  return (
    <div data-testid="gae-section">
      <Card style={{ padding: 18, background: '#FFFFFF' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: '#6B7B72' }}>
              Phase 1 — Publication-ready Enhancements
            </div>
            <h2 style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic',
                          fontWeight: 700, fontSize: 28, color: '#1F3A2E', margin: '4px 0 0' }}>
              General Analysis — Phenotypic & Genotypic Path with QA Dashboard
            </h2>
          </div>
          <Button
            data-testid="gae-csv-bundle-download"
            onClick={downloadCSVBundle}
            disabled={!analysisId}
            style={{ background: '#4A7C59', color: 'white' }}
          >
            <Download size={14} style={{ marginRight: 6 }} />
            Download CSV bundle (.zip)
          </Button>
          <Button
            data-testid="gae-publication-bundle-open"
            onClick={() => setPubBundleConfig((c) => ({ ...c, open: true, err: null }))}
            disabled={!analysisId}
            style={{ background: '#2C5530', color: 'white' }}
          >
            <Download size={14} style={{ marginRight: 6 }} />
            Download Publication Figures Bundle (TIFF, 600 DPI)
          </Button>
          <Button
            data-testid="gae-cite-this-analysis"
            onClick={() => {
              const qa = data.qa_dashboard || {};
              const meta = data.meta || {};
              const date = new Date().toISOString().slice(0, 10);
              const cite = (
                `BKAgriD General Analysis Phase 1 (${date}). ` +
                `n=${meta.n_observations} obs · ${meta.n_genotypes} genotypes · ` +
                `${meta.n_replications} reps; dep=${meta.dependent_variable}. ` +
                `Quality-assurance composite score: ${qa.total_score}/${qa.total_max} ` +
                `(${qa.score_100}/100, ${(qa.verdict_color || '').toUpperCase()}). ` +
                `Generated by BKAgriD; methods follow Singh & Chaudhary 1985 and ` +
                `Wright 1934. https://bkagrid.com.`
              );
              try {
                navigator.clipboard.writeText(cite);
              } catch (_) { /* noop */ }
            }}
            style={{ background: '#FFFFFF', color: '#1F3A2E', border: '1px solid #4A7C59', marginLeft: 6 }}
            title="Copy a one-line APA-style citation including this analysis's QA score"
          >
            Cite this analysis
          </Button>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8, color: '#3F4D44', fontSize: 12 }}>
          <span><strong>n =</strong> {meta.n_observations}</span>
          <span><strong>Genotypes:</strong> {meta.n_genotypes}</span>
          <span><strong>Reps:</strong> {meta.n_replications}</span>
          <span><strong>Predictors:</strong> {(meta.predictors || []).join(', ')}</span>
          <span><strong>Dep:</strong> {meta.dependent_variable}</span>
        </div>
      </Card>

      <SectionTitle>A. Quality Assurance Dashboard</SectionTitle>
      <QADashboard qa={data.qa_dashboard} />

      <SectionTitle>B. Variance Components</SectionTitle>
      <VarianceComponentsTable vc={data.variance_components} />

      <SectionTitle>C. Correlation Matrices</SectionTitle>
      <CorrelationMatrixTable matrix={data.correlation_matrices?.pearson}
                                title="Pearson (raw observations)" />
      <CorrelationMatrixTable matrix={data.correlation_matrices?.phenotypic}
                                title="Phenotypic rp (entry-mean basis)" />
      <CorrelationMatrixTable matrix={data.correlation_matrices?.genotypic_raw}
                                title="Genotypic rg (RAW — values may exceed ±1)" />
      <CorrelationMatrixTable matrix={data.correlation_matrices?.genotypic_bounded}
                                title="Genotypic rg (Fisher-z bounded to ±0.999)" />
      <Caption>{data.correlation_matrices?.caption}</Caption>

      <h3 style={{
        fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic',
        fontWeight: 600, fontSize: 18, color: '#1F3A2E',
        margin: '18px 0 6px',
      }}>
        C1. Correlation Heatmaps (Pearson | rp | rg)
      </h3>
      <HeatmapPanel
        analysisId={analysisId}
        mantelRows={mantelData?.rows}
        genoInvalid={mantelData?.geno_matrix_invalid}
      />

      <SectionTitle>D. Matrix Validity Diagnostics</SectionTitle>
      <MatrixDiagnosticsTable md={data.matrix_diagnostics} />

      <SectionTitle>E. Path Analysis (Wright 1934)</SectionTitle>
      <PathTable path={data.paths?.pearson} label="Pearson Path"
                  analysisId={analysisId} pathKey="pearson" />
      <PathTable path={data.paths?.phenotypic} label="Phenotypic Path"
                  analysisId={analysisId} pathKey="phenotypic" />
      <PathTable path={data.paths?.genotypic} label="Genotypic Path"
                  analysisId={analysisId} pathKey="genotypic" />

      <SectionTitle>F. Variance Inflation Factor (VIF)</SectionTitle>
      <VIFTable vif={data.vif?.pearson} label="Pearson predictor matrix" />
      <VIFTable vif={data.vif?.phenotypic} label="Phenotypic predictor matrix" />
      <VIFTable vif={data.vif?.genotypic} label="Genotypic predictor matrix (raw)" />

      <SectionTitle>F1. PCA Biplot (Gabriel 1971)</SectionTitle>
      <BiplotPanel analysisId={analysisId} />

      <SectionTitle>F2. Cluster Dendrograms (UPGMA &amp; Ward)</SectionTitle>
      <DendrogramPanel analysisId={analysisId} />

      <RobustnessConsensusCard
        analysisId={analysisId}
        bootData={bootData}
        loocvData={loocvData}
        seqData={seqData}
      />

      <div id="section-h" />
      <SectionTitle>H. Bootstrap Confidence Intervals (BCa, Efron-Tibshirani 1993)</SectionTitle>
      <BootstrapPanel analysisId={analysisId} onResult={setBootData} />

      <div id="section-i" />
      <SectionTitle>I. Leave-One-Genotype-Out Cross-Validation (Stone 1974)</SectionTitle>
      <LOOCVPanel analysisId={analysisId} onResult={setLoocvData} />

      <div id="section-j" />
      <SectionTitle>J. Sequential Path Analysis (Bhatt 1973)</SectionTitle>
      <SequentialPathPanel analysisId={analysisId} onResult={setSeqData} />

      <div id="section-k" />
      <SectionTitle>K. Smith-Hazel Selection Index (Smith 1936; Hazel 1943)</SectionTitle>
      <SmithHazelPanel
        analysisId={analysisId}
        traitsHint={data.meta?.traits || (seqData?.meta?.traits) || (bootData?.meta?.predictors) || []}
        classification={(() => {
          // Build trait -> bucket map from the 3 robustness method results
          // Same logic as RobustnessConsensusCard but exposed as a dict.
          if (!(bootData && loocvData && seqData)) return {};
          const traits = seqData.meta?.predictors || [];
          const out = {};
          for (const pp of traits) {
            const cls = _classifyPredictor(bootData, loocvData, seqData, pp);
            out[pp] = cls.bucket;
          }
          return out;
        })()}
      />

      <div id="section-l" />
      <SectionTitle>L. Cluster Stability Plot (Hubert-Arabie 1985; Milligan-Cooper 1985)</SectionTitle>
      <ClusterStabilityPanel analysisId={analysisId} />

      <div id="section-m" />
      <SectionTitle>M. Email me the bundle (Resend)</SectionTitle>
      <EmailBundlePanel analysisId={analysisId} defaultEmail={data.user_email || ''} />

      <SectionTitle>G. Significance Test for rg (Robertson 1959)</SectionTitle>
      <RgSignificanceTable rg={data.rg_significance} />

      <SectionTitle>H. Sample-size Adequacy</SectionTitle>
      <Card style={{ padding: 12, background: '#FAF8F2' }} data-testid="gae-sample-size">
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#3F4D44' }}>
          <li>
            Tabachnick & Fidell 2013 (n ≥ 50 + 8k = {data.sample_size?.tabachnick_target}):{' '}
            <strong style={{ color: data.sample_size?.tabachnick_pass ? '#166737' : '#B11F1F' }}>
              {data.sample_size?.tabachnick_pass ? 'PASS' : 'FAIL'}
            </strong>
            {' '}(observed n = {data.sample_size?.n_observations})
          </li>
          <li>
            Hair 2010 minimum (n/k ≥ 10): <strong>{data.sample_size?.n_to_k_pass_min ? 'PASS' : 'FAIL'}</strong>
            {' '}(ratio = {data.sample_size?.n_to_k_ratio})
          </li>
          <li>
            Preferred (n/k ≥ 20): <strong>{data.sample_size?.n_to_k_pass_preferred ? 'PASS' : 'FAIL'}</strong>
          </li>
          <li>
            rg sampling stability (n_genotypes ≥ 20): <strong>{data.sample_size?.n_genos_pass_rg ? 'PASS' : 'FAIL'}</strong>
            {' '}(observed = {data.sample_size?.n_genotypes})
          </li>
        </ul>
        <Caption>{data.sample_size?.recommendation}</Caption>
      </Card>

      {data.methodology_appendix && (
        <>
          <SectionTitle>Methodology Appendix (auto-generated)</SectionTitle>
          <Card style={{ padding: 14, background: '#FAF6EE' }} data-testid="gae-methodology-appendix">
            <details>
              <summary style={{ cursor: 'pointer', fontSize: 13, color: '#1F3A2E', fontWeight: 600 }}>
                Software, methods, references, reproducibility statement, citation
              </summary>
              <div style={{ marginTop: 10, fontSize: 12, color: '#3F4D44' }}>
                <p style={{ fontStyle: 'italic', color: '#6B7B72' }}>{data.methodology_appendix.caption}</p>
                <h5 style={{ fontWeight: 700, marginTop: 12 }}>1. Software Information</h5>
                <ul>
                  {Object.entries(data.methodology_appendix.software_information || {}).map(([k, v]) => (
                    <li key={k}><strong>{k}:</strong> {String(v)}</li>
                  ))}
                </ul>
                <h5 style={{ fontWeight: 700, marginTop: 12 }}>2. Data Information</h5>
                <ul>
                  {Object.entries(data.methodology_appendix.data_information || {}).map(([k, v]) => (
                    <li key={k}><strong>{k}:</strong> {String(v)}</li>
                  ))}
                </ul>
                <h5 style={{ fontWeight: 700, marginTop: 12 }}>3. Statistical Methods Used</h5>
                {(data.methodology_appendix.statistical_methods || []).map((m, i) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    <strong>{m.name}</strong> — {m.method}
                    {m.formulas && (
                      <ul>
                        {m.formulas.map((f, j) => (
                          <li key={j} style={{ fontFamily: 'serif', fontStyle: 'italic' }}>{f}</li>
                        ))}
                      </ul>
                    )}
                    {m.references && (
                      <div style={{ color: '#6B7B72', fontSize: 11 }}>References: {m.references.join('; ')}</div>
                    )}
                    {m.note && (<div style={{ fontStyle: 'italic', fontSize: 11, color: '#6B7B72' }}>{m.note}</div>)}
                  </div>
                ))}
                <h5 style={{ fontWeight: 700, marginTop: 12 }}>4. References</h5>
                <ol>
                  {(data.methodology_appendix.references || []).map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ol>
                <h5 style={{ fontWeight: 700, marginTop: 12 }}>5. Reproducibility</h5>
                <p>{data.methodology_appendix.reproducibility_statement}</p>
                <h5 style={{ fontWeight: 700, marginTop: 12 }}>6. Citation</h5>
                <p style={{ fontStyle: 'italic' }}>{data.methodology_appendix.citation}</p>
              </div>
            </details>
          </Card>
        </>
      )}

      <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 6, color: '#6B7B72', fontSize: 11 }}>
        <CheckCircle2 size={14} />
        <em>Auto-generated by BKAgriD General Analysis Phase 1 · Singh & Chaudhary 1985 · Wright 1934 · Robertson 1959 · Belsley-Kuh-Welsch 1980</em>
      </div>

      {pubBundleConfig.open && (
        <div
          data-testid="gae-publication-bundle-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(31, 58, 46, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => !pubBundleConfig.busy
            && setPubBundleConfig((c) => ({ ...c, open: false }))}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#FAF6EE', borderRadius: 10, padding: '22px 26px',
              maxWidth: 540, width: '90%', boxShadow: '0 8px 28px rgba(0,0,0,0.2)',
              fontFamily: '"Times New Roman", Georgia, serif',
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1F3A2E', margin: '0 0 4px' }}>
              Publication Figures Bundle
            </h3>
            <p style={{ fontSize: 12, color: '#5A6B62', marginTop: 0 }}>
              12 figures (QA dashboard, 3 path diagrams, 3 heatmaps, 2 biplots, 3 dendrograms) as
              high-DPI TIFFs with sRGB profile + Figure_Manifest.csv +
              {pubBundleConfig.include_captions ? ' paste-ready Figure_Captions.docx +' : ''} README.
            </p>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1F3A2E', marginTop: 10 }}>
              Resolution
            </label>
            <div data-testid="gae-publication-bundle-dpi" style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              {[300, 600, 1200].map((d) => (
                <label key={d} style={{ fontSize: 12, color: '#3F4D44', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="pub-dpi"
                    checked={pubBundleConfig.dpi === d}
                    onChange={() => setPubBundleConfig((c) => ({ ...c, dpi: d }))}
                    style={{ marginRight: 4 }}
                    data-testid={`gae-publication-bundle-dpi-${d}`}
                  />
                  {d} DPI{d === 600 ? ' (recommended)' : (d === 1200 ? ' (poster)' : ' (web)')}
                </label>
              ))}
            </div>
            {pubBundleConfig.dpi === 1200 && (
              <div data-testid="gae-publication-bundle-dpi-warn" style={{ marginTop: 6, padding: 6, background: '#FFF6E0', color: '#8B5A0A', border: '1px solid #E5C46B', borderRadius: 4, fontSize: 11, fontStyle: 'italic' }}>
                {'1200 DPI renders 12 figures totaling ~32 MB and takes 90-120 seconds. On hosted preview the request may exceed the proxy timeout (\u224860s). Use 600 DPI (publication-standard) unless you specifically need 1200 DPI for poster printing.'}
              </div>
            )}

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1F3A2E', marginTop: 14 }}>
              Naming convention
            </label>
            <select
              data-testid="gae-publication-bundle-naming"
              value={pubBundleConfig.naming_convention}
              onChange={(e) => setPubBundleConfig((c) => ({ ...c, naming_convention: e.target.value }))}
              style={{ width: '100%', padding: '6px 8px', marginTop: 4, fontSize: 13, border: '1px solid #C9C2B0', borderRadius: 4, background: 'white' }}
            >
              <option value="crop_science">Crop Science style (Fig_S1_*, Fig_S2_*)</option>
              <option value="indian_journal">Indian Journal of Genetics (Figure_1.tiff, Figure_2.tiff)</option>
              <option value="tag">TAG style (Fig1a, Fig1b)</option>
              <option value="generic">Generic numbered (figure_01.tiff)</option>
            </select>

            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#3F4D44', marginTop: 14, cursor: 'pointer' }}>
              <input
                type="checkbox"
                data-testid="gae-publication-bundle-captions"
                checked={pubBundleConfig.include_captions}
                onChange={(e) => setPubBundleConfig((c) => ({ ...c, include_captions: e.target.checked }))}
              />
              Include Figure_Captions.docx (paste-ready manuscript captions)
            </label>

            {pubBundleConfig.err && (
              <div data-testid="gae-publication-bundle-err" style={{ marginTop: 12, padding: 8, background: '#FBEAEA', color: '#B11F1F', borderRadius: 4, fontSize: 12 }}>
                {pubBundleConfig.err}
              </div>
            )}

            {pubBundleConfig.busy && (
              <div data-testid="gae-publication-bundle-busy" style={{ marginTop: 12, padding: 8, background: '#E8F4ED', color: '#166737', borderRadius: 4, fontSize: 12 }}>
                Rendering 12 figures at {pubBundleConfig.dpi} DPI... this can take 30-60 seconds for high resolutions.
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <Button
                data-testid="gae-publication-bundle-cancel"
                onClick={() => setPubBundleConfig((c) => ({ ...c, open: false }))}
                disabled={pubBundleConfig.busy}
                style={{ background: '#EFEAE0', color: '#1F3A2E', border: '1px solid #C9C2B0' }}
              >
                Cancel
              </Button>
              <Button
                data-testid="gae-publication-bundle-download"
                onClick={downloadPublicationBundle}
                disabled={pubBundleConfig.busy}
                style={{ background: '#2C5530', color: 'white' }}
              >
                <Download size={14} style={{ marginRight: 6 }} />
                {pubBundleConfig.busy ? 'Rendering...' : 'Generate & Download'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralAnalysisEnhancedView;
